// =============================================================================
// ShipmentEntry — guided "Generate & Ship" wizard.
//
// Mirrors Documents/shipment-flow-simulator.html, wired to the real
// ShipmentEntryController endpoints. Walks a clerk from an open sales order
// through to a bought FedEx label + shipped Epicor pack + printed pack slip,
// covering every scenario the simulator documents:
//
//   Stage 1  Order        GET  /api/sales-orders/{orderNum}?include=shipments
//   Stage 2  Lines        GET  /api/shipment-entry/pickers/bins|lots
//                         POST /api/shipment-entry                (create pack)
//                         POST /api/shipment-entry/{pack}/lines   (per line)
//   Stage 3  Review       GET  /api/shipment-entry/{pack}/readiness
//                         POST /api/fedex/address/validate
//   Stage 4  Service      GET  /api/fedex/services (unused — readiness serves the catalog)
//   Stage 5  Generate     POST /api/shipment-entry/{pack}/generate-and-ship
//                         GET  /api/reports/packslip/{packNum}     (our own pack slip PDF)
//
// Contract-matching notes baked into this file:
//   • CreatePack.orderNum is a STRING; AddLine.orderNum is a NUMBER.
//   • readiness / pickers / services / pack-slip report are RAW responses
//     ({success|ready} at top level), NOT the {success,jobId,data} JobResult
//     envelope used by create/add/generate.
//   • Division (Character03) is required to create a pack and has no source on
//     the order — supplied from env config (SHIP_DIVISION).
//   • Package weight/dims are OMITTED from requests unless the clerk supplies
//     them; the server then falls back to ShipHead.Weight / PkgLength / … .
// =============================================================================
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spinner, Button, Alert, Form } from "react-bootstrap";
import {
  FaTruck, FaChevronLeft, FaCheckCircle, FaExclamationTriangle,
  FaFilePdf, FaFileInvoice, FaSyncAlt,
} from "react-icons/fa";
import { apiFetch } from "./utils/auth";
import { buildShippableRows, shipBlockReason as computeShipBlockReason } from "./utils/shipGate";
import "./pagecss/Webappcss.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// Does a committed pack line correspond to this shippable order row?
// Compared as strings on purpose: rows come from the order DTO, but a RESUMED
// pack's lines come from CustShip GetByID, and the two aren't guaranteed to
// agree on number-vs-string for OrderLine / OrderRelNum. A strict === that
// silently returns false here would re-add a line the pack already holds.
const sameLine = (a, b) =>
  String(a.orderLine) === String(b.orderLine)
  && String(a.orderRelNum) === String(b.orderRelNum);

// ── Formatting helpers ───────────────────────────────────────────────────────
function money(v, symbol = "$") {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  const n = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v));
  return `${symbol}${n}`;
}
// What to say about freight on the Done screen.
//
// Mirrors the rule the backend already applies when it composes the pack-slip ship
// comment (ShipCommentComposer.cs) — deliberately, so the screen and the document the
// same shipment produces cannot contradict each other:
//
//   • A figure is stated ONLY when Shukla is the payer AND the charge is > 0. A label
//     can come back with a null or zero net charge, and — the backend's own words —
//     "Freight: 0.00 is a false statement". FedEx Priority Overnight is never free;
//     a zero here means FedEx returned no amount (sandbox, or an unrated reply), not
//     that the shipment cost nothing.
//   • On RECIPIENT / THIRD_PARTY / COLLECT, FedEx bills somebody else's account at
//     THEIR negotiated rate — one we never see. Our number would be our own rate or
//     zero, so name the payer instead of printing a figure nobody will be charged.
function freightLine(result) {
  const payer = (result?.paymentType || "SENDER").trim().toUpperCase();
  switch (payer) {
    case "RECIPIENT":   return "Billed to the recipient's FedEx account";
    case "THIRD_PARTY": return "Billed to a third-party FedEx account";
    case "COLLECT":     return "Collect — payable to FedEx on delivery";
    case "SENDER": {
      const n = Number(result?.netCharge);
      return Number.isFinite(n) && n > 0
        ? `${money(n, "$")} ${result.currency || ""}`.trim()
        : "Carrier charge not returned — cost recorded on pack";
    }
    default: return "Billed to another FedEx account";
  }
}
function fmtQty(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(Number(v));
}

// ── Static catalogue -------------------------------------------------------
// Billing variants exactly as the orchestrator accepts them. SENDER prepays
// on Shukla's own account; the other three bill a customer FedEx account, so
// they require a payor account number.
const PAYMENT_TYPES = [
  { value: "SENDER",      label: "SENDER",      hint: "Shukla prepays" },
  { value: "THIRD_PARTY", label: "THIRD PARTY", hint: "Customer pays" },
  { value: "RECIPIENT",   label: "RECIPIENT",   hint: "Bill on delivery" },
  { value: "COLLECT",     label: "COLLECT",     hint: "Pay at pickup" },
];
const NEEDS_PAYOR_ACCOUNT = (p) => p && p !== "SENDER";

// ── Deployment-specific values ───────────────────────────────────────────────
// Neither has a source on any endpoint, so they can't be fetched — but they are
// site configuration, not application logic, so they live in the environment
// with the current Shukla values as defaults.
const SHIP_DIVISION = process.env.REACT_APP_SHIP_DIVISION || "SM-SALES";
const SHIP_VIA_CREATE_CODE = process.env.REACT_APP_SHIP_VIA_CREATE_CODE || "BPO";

// ── Palette ------------------------------------------------------------------
// Matches the simulator's brand tokens. Purple is the wizard's primary; orange
// is reserved for the single irreversible commit action (buy label). These were
// previously written inline as `var(--fedex-orange, #FF6600)` against a variable
// that is defined nowhere, so every primary control silently fell back to solid
// orange and the whole wizard read as an orange page.
const PURPLE = "#4D148C";
const PURPLE_DARK = "#2E0A56";
const ORANGE = "#FF6600";
const SEL_BORDER = "#D8B4FE";   // purple-300 — selected card outline
const SEL_BG = "#FAF5FF";       // purple-50  — selected card fill
const IDLE_BORDER = "#EEF0F3";

// ── Service presentation ------------------------------------------------------
// FedEx transit commitments are published service definitions, not Shukla data,
// and no endpoint returns them — so this is display-only copy keyed by the
// serviceType the API sends. Anything not listed simply renders without a
// transit line; nothing here affects ordering, selection, or the request.
const SERVICE_BLURBS = {
  PRIORITY_OVERNIGHT: "Next business day by 10:30 AM",
  STANDARD_OVERNIGHT: "Next business day end of day",
  FEDEX_2_DAY:        "2 business days",
  FEDEX_2_DAY_AM:     "2 business days by noon",
  FEDEX_GROUND:       "1–5 business days",
  FIRST_OVERNIGHT:    "Next business day by 8:00 AM",
};
// How many services to show before the "more services" disclosure, used only
// when the server reports none as stampable.
const CORE_SERVICE_COUNT = 4;

// ── Error formatting ─────────────────────────────────────────────────────────
// The Add-Line / Update-Line / Mark-Shipped handlers deliberately append a
// diagnostic dump to the failure reason:
//
//     <human reason>\n--- DEBUG CONTEXT ---\n<multi-KB JSON>
//
// That JSON is for whoever is debugging an Epicor rejection, not for the clerk
// staring at the screen. Split it: lead with the reason, keep the dump behind a
// disclosure so it is still one click away (and still copy-pasteable) rather
// than deleted.
// Two markers exist in the API and BOTH have to be cut.
//
// The Epicor slices (AddLine, UpdateLine, MarkShipped) append "--- DEBUG CONTEXT ---";
// the FedEx slices (Rates, Label, Void, Locations) append "--- DEBUG ---". Only the
// first was ever stripped here, so every FedEx failure carried its diagnostic blob
// into the message classifier — and that blob embeds SentJson, which is full of the
// word "address". The address rule below then matched on the DEBUG DUMP rather than
// on the error, and a clerk was told to re-check the delivery address for a 503
// outage and again for a 403 credentials failure. Two different wrong answers from
// one missing string.
//
// Searched as a list, cutting at whichever appears first, so a message carrying both
// still loses everything from the earliest one on.
const DEBUG_MARKERS = ["--- DEBUG CONTEXT ---", "--- DEBUG ---"];

/// Index of the first debug marker in `text`, or -1 when there is none.
function debugMarkerIndex(text) {
  let at = -1;
  for (const m of DEBUG_MARKERS) {
    const i = text.indexOf(m);
    if (i !== -1 && (at === -1 || i < at)) at = i;
  }
  return at;
}
// Anything past this is a wall of text regardless of whether it carries the
// marker — Epicor also returns bare serialized exceptions with no marker at all.
const SUMMARY_MAX = 400;

// ── Turning backend text into something a clerk can act on ───────────────────
// Backend messages are written for whoever is debugging Epicor: they name API
// routes, .NET exception types, Epicor programs and BO methods. None of that
// helps the person at the packing bench, and some of it (endpoint paths) is
// internal detail that shouldn't be on screen at all.
//
// Two passes:
//   1. RULES — recognise the specific failures this flow produces and state
//      them in business terms, including what the clerk should do next.
//   2. SANITISE — for anything unrecognised, strip the technical debris rather
//      than dumping it, so a new backend message degrades to plain prose
//      instead of leaking a stack trace.
//
// The raw text is never destroyed; it goes to the browser console for support.
/// True when the text carries bytes no person wrote — control characters or the
/// U+FFFD replacement mark a compressed body leaves when read as text (UI-SHP-057).
/// A char-code scan rather than a regex, so no control characters sit in a literal.
function hasUnreadableBytes(text) {
  for (const ch of String(text ?? "")) {
    const c = ch.charCodeAt(0);
    if (c === 0xfffd || (c < 0x20 && c !== 0x09 && c !== 0x0a && c !== 0x0d)) return true;
  }
  return false;
}

const MESSAGE_RULES = [
  {
    // The BPM soft-fail trap. This is a SUCCESS — the orchestrator verified
    // the pack really is SHIPPED — so it must not read like a failure.
    test: /soft-fail trap|reported failure but Epicor shows/i,
    text: "Epicor reported an error while marking the shipment, but the shipment saved correctly and is confirmed shipped. No action needed.",
  },
  {
    // This warning is about Epicor's OWN printer copy (the SSRS PackingSlip report the
    // orchestrator still submits server-side), NOT the Pack Slip PDF on this screen —
    // that one is now generated by us and is unaffected. Worth saying plainly, because a
    // note reading "couldn't be printed" sitting directly under a working Pack Slip PDF
    // is the kind of contradiction that turns into a support call.
    test: /pack ?slip submission failed|rejected the Pack Slip submission|refused the pack slip print request/i,
    text: "Epicor couldn't queue its own printer copy of the packing slip. The shipment, the label and the Pack Slip PDF above are all fine — only the Epicor-printed copy is affected.",
  },
  {
    test: /negative onhand quantity/i,
    // Keep Epicor's own wording here: it is already business language and it
    // names the bin, which is exactly what the clerk needs.
    keepOriginal: true,
  },
  {
    test: /already has a label|already shipped|already marked SHIPPED/i,
    text: "This pack has already been shipped. To ship it again, turn on “Force re-ship” so the existing label is voided first.",
  },
  {
    test: /could not determine FedEx service/i,
    text: "No FedEx service could be matched to this order's shipping method. Pick a service on the previous step.",
  },
  {
    // FedEx quoted no price for this lane. Distinct from the address rule below
    // and it MUST sit above it: the run dies at RateShop, before the address is
    // ever evaluated, so sending the clerk to re-check a verified address is
    // simply wrong. Retrying does not help either — the carrier's answer will be
    // the same until the service or the destination changes.
    test: /quoted no price|no rate-quote token was issued|returned no priced services/i,
    text: "FedEx quoted no price for this shipment, so no label could be bought and nothing was shipped. This is usually a service or destination the account can't ship — try a different service, or check the delivery address.",
  },
  {
    // ── Read this before trusting a 503 from FedEx ──────────────────────────
    // FedEx's gateway answers a request it cannot MATCH with
    // `503 SERVICE.UNAVAILABLE.ERROR` — the same body it would send if it were
    // genuinely down. A 503 here is therefore NOT evidence of an outage.
    //
    // Measured against the sandbox on 2026-09-14: a minimal documented payload
    // returned 200 with 5 rates while our real payload returned 503 on the same
    // token seconds apart. But the 200 carried alert VIRTUAL.RESPONSE and echoed
    // a customerTransactionId we never sent, and 5lb / 500lb / FL->FL / FL->AK
    // all returned the identical $20.53 and the identical transactionId. The
    // sandbox is a FIXTURE SERVER: it replays canned scenarios and 503s anything
    // it has no fixture for. Adding any of 14 individually VALID fields
    // (residential:false, today's shipDateStamp, serviceType, dimensions, the
    // street address itself) to a working payload flipped 200 into 503.
    //
    // So: in sandbox this means "no fixture matched your real address", and no
    // payload edit fixes it — only real credentials do. In production it would
    // mean a genuine carrier problem. Either way the clerk cannot fix it and
    // must not be told to re-check an address FedEx never looked at.
    test: /service is (?:currently|temporarily) unavailable|SERVICE\.UNAVAILABLE|check back at a later time|bad gateway|gateway time-?out/i,
    text: "No shipping rate could be obtained from FedEx, so nothing was shipped and no label was bought. Retrying is unlikely to help — please pass this to IT.",
  },
  {
    // UI-SHP-057: FedEx refused the chosen SERVICE for this lane (seen with
    // International Economy to a US address). Above the address rule: the
    // refusal names origin/destination and must not send the clerk to re-check
    // a delivery address that is fine.
    test: /service(?:\s*type)? (?:is )?not (?:allowed|available|supported|valid)|invalid service|SERVICE\.?TYPE|SERVICE\.NOT\.?ALLOWED|NOTALLOWED|not allowed for (?:the )?(?:origin|destination)/i,
    text: "FedEx doesn't offer the chosen service for this delivery address (for example an international service to a US address), so nothing was shipped and no label was bought. Pick a different service and try again.",
  },
  {
    // UI-SHP-057: a reply we could not read (FedEx's gzip error body printed as
    // bytes). Never show the bytes; the raw text still goes to the console.
    test: { test: (t) => /could not be read/i.test(t) || hasUnreadableBytes(t) },
    text: "FedEx refused this request, and its reply could not be read. Nothing on this screen needs changing — please pass this to IT.",
  },
  {
    // FedEx refused the CREDENTIALS, not the shipment. Seen live on the first
    // production label attempt: rating succeeded, a rate quote was issued, and
    // POST /ship/v1/shipments answered 403 "We could not authorize your
    // credentials" — because the Ship API needs Shipping Label Certification on
    // the account while Rate & Transit Times needs none. So the account can
    // price a parcel and not print for it, which looks bizarre from the screen
    // unless the message says so.
    //
    // Above the address rule on purpose: the 403 body embeds the shipment JSON,
    // so it is full of the word "address" and would otherwise be read as a bad
    // delivery address — sending the clerk to fix something that is correct.
    test: /could not authorize your credentials|NOT\.AUTHORIZED|FORBIDDEN\.ERROR|check your permissions/i,
    text: "FedEx refused our account's permission to create labels — nothing is wrong with this shipment or its address. The account can get rates but isn't cleared to print labels yet. Pass this to IT.",
  },
  {
    test: /address|recipient|streetLines|postalCode|stateOrProvince/i,
    text: "FedEx wouldn't accept the delivery address. Check the recipient's street, city, state, postal code and phone, then try again.",
  },
];

/// Strip the orchestrator's own bookkeeping off a failure reason.
///
/// Runs BEFORE humanizeMessage, not inside sanitizeMessage, and that ordering
/// matters: sanitizeMessage judges whether stripping wrecked the sentence by
/// how much text survived, and these two removals are large, deliberate and
/// harmless. Folded into that ratio they push otherwise-readable messages
/// under the threshold and collapse them to the generic "something went wrong".
/// Stripping first makes the cleaned text the baseline the ratio is measured
/// against. No MESSAGE_RULE keys on a step name, so removing the prefix
/// cannot change which rule matches.
function stripBookkeeping(text) {
  return String(text ?? "")
    // The child job id is how support correlates the run in ErpJob. It is
    // already in the console dump and means nothing to a clerk, so it must not
    // be the last thing they read on a failure card.
    .replace(/;?\s*childJobId\s*=\s*[0-9a-fA-F-]{8,}/g, "")
    // Internal step names leading an orchestrator failure ("RateShop failed:").
    .replace(/^\s*(?:RateShop|BuyLabel|CreatePack|MarkShipped|VoidLabel|PrintPackSlip)\s+failed:\s*/i, "")
    // The vendor seam's own wording (UI-SHP-057): "HTTP 400: …" and "HTTP 503 on a
    // POST to fedex/rate. The answer is lost, not negative: …" — status codes and
    // routes, never a sentence for the clerk.
    .replace(/\bHTTP \d{3} on a [A-Z]+ to [\w./-]+\.\s*(?:The answer is lost[^.]*\.)?\s*/gi, "")
    .replace(/(^|\s)HTTP \d{3}:?\s*/gi, "$1")
    .trim();
}

function sanitizeMessage(text) {
  return String(text ?? "")
    // Endpoint references, with or without an HTTP verb.
    .replace(/\b(?:GET|POST|PUT|PATCH|DELETE)\s+\/\S+/gi, "")
    .replace(/\/api\/\S+/gi, "")
    // Absolute URLs.
    .replace(/https?:\/\/\S+/gi, "")
    // .NET / Epicor type and member names.
    .replace(/\b\w+Exception\b/g, "")
    .replace(/\bErp\.[\w.]+/g, "")
    .replace(/\bIce\.[\w.]+/g, "")
    .replace(/\b[\w.]+\.dll\b/gi, "")
    .replace(/\bSee more info in the Inner Exception section of Exception Details\.?/gi, "")
    // Tidy the punctuation the removals leave behind.
    .replace(/\s*—\s*(?=[.,]|$)/g, "")
    .replace(/\(\s*\)/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,;:])/g, "$1")
    .replace(/[.,;:\-—\s]+$/g, "")
    .trim();
}

function humanizeMessage(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return "";
  for (const rule of MESSAGE_RULES) {
    if (rule.test.test(text)) {
      if (rule.keepOriginal) break;
      return rule.text;
    }
  }
  const cleaned = sanitizeMessage(text);
  // If stripping removed so much that what's left isn't a sentence any more
  // ("threw while calling — retry via"), a generic line is more use than
  // shredded prose. Judge by how much survived, not just by length.
  const survived = cleaned.length / Math.max(text.length, 1);
  if (!cleaned || cleaned.length < 25 || survived < 0.5) {
    return "Something went wrong while completing this step. Please try again, or contact support if it keeps happening.";
  }
  return /[.!?]$/.test(cleaned) ? cleaned : `${cleaned}.`;
}

/// Reduce any backend message to the one sentence the clerk needs.
/// The debug dump is dropped from the UI entirely — never rendered, not even
/// collapsed — and written to the console so support can still retrieve it.
function toDisplayMessage(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return "Something went wrong. Please try again.";

  // Drop the diagnostic dump before anything else looks at the string.
  const i = debugMarkerIndex(text);
  const head = stripBookkeeping(i === -1 ? text : text.slice(0, i));

  let message = humanizeMessage(head);
  // Nothing left once the seam's status line is cut (UI-SHP-057) — never a blank card.
  if (!message) return "Something went wrong. Please try again, or contact support if it keeps happening.";

  // Backstop: if a rule didn't match and the remaining prose is still an essay,
  // keep the first sentence or two rather than filling the card.
  if (message.length > SUMMARY_MAX) {
    const window = message.slice(0, SUMMARY_MAX);
    const dot = window.lastIndexOf(". ");
    message = dot > SUMMARY_MAX / 2 ? message.slice(0, dot + 1) : `${window.trim()}…`;
  }
  return message;
}

/// Renders a backend failure/warning as plain language. `raw` is kept out of
/// the DOM; it goes to the console under a stable prefix support can ask for.
function ErrorBody({ raw }) {
  const message = toDisplayMessage(raw);
  useEffect(() => {
    const text = String(raw ?? "").trim();
    // Only worth logging when we actually hid something.
    if (text && text !== message) {
      // eslint-disable-next-line no-console
      console.info("[ShipmentEntry] backend detail (hidden from UI):", text);
    }
  }, [raw, message]);
  return (
    <div style={{ overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0 }}>{message}</div>
  );
}

// ── Shipment summary card ────────────────────────────────────────────────────
// The simulator's label preview, rendered from what the orchestrator says went
// on the parcel (response.shipper / .recipient / .weight / .customerReference)
// — NOT re-derived from the order, because an override, the pack and the order
// can all disagree and only these values were actually printed.
//
// This sits alongside the real FedEx PDF, it does not replace it: the PDF is
// the scannable artifact that ships, this is the at-a-glance confirmation.
function addressLines(p) {
  if (!p) return [];
  const cityLine = [p.city, [p.stateOrProvinceCode, p.postalCode].filter(Boolean).join(" ")]
    .filter(Boolean).join(", ");
  return [
    p.companyName,
    // personName is dropped server-side when it duplicates the company, so
    // anything here is a genuinely distinct contact.
    p.personName,
    ...(p.streetLines || []),
    cityLine,
    p.countryCode && p.countryCode !== "US" ? p.countryCode : null,
  ].filter(Boolean);
}

function ShipmentLabelCard({ result, orderNum }) {
  const from = addressLines(result.shipper);
  const to = addressLines(result.recipient);
  const shipDate = result.shipDate ? new Date(result.shipDate).toLocaleDateString("en-US") : "—";
  const billing = [result.paymentType, result.payorAccountMasked && `acct ${result.payorAccountMasked}`]
    .filter(Boolean).join(" · ");
  const cell = { fontSize: 12, lineHeight: 1.45 };

  return (
    <div style={{ border: "2px solid #0F172A", borderRadius: 4, padding: 14, background: "#fff", maxWidth: 520 }}>
      {/* FROM + ship date / weight */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, paddingBottom: 10, borderBottom: "1px solid #0F172A" }}>
        <div style={{ minWidth: 0 }}>
          <div className="qw-eyebrow">From</div>
          {from.length ? from.map((l, i) => (
            <div key={i} style={{ ...cell, fontWeight: i === 0 ? 700 : 400 }}>{l}</div>
          )) : <div style={cell}>—</div>}
          {result.shipper?.phoneNumber && <div style={cell}>{result.shipper.phoneNumber}</div>}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div className="qw-eyebrow">Ship date</div>
          <div style={{ ...cell, fontWeight: 700 }}>{shipDate}</div>
          <div className="qw-eyebrow" style={{ marginTop: 6 }}>Weight</div>
          <div style={{ ...cell, fontWeight: 700 }}>
            {Number(result.weight) > 0 ? `${fmtQty(result.weight)} ${result.weightUOM || "LB"}` : "—"}
          </div>
        </div>
      </div>

      {/* TO */}
      <div style={{ padding: "10px 0", borderBottom: "1px solid #0F172A" }}>
        <div className="qw-eyebrow">To</div>
        {to.length ? to.map((l, i) => (
          <div key={i} style={{ ...cell, fontWeight: i === 0 ? 700 : 400, fontSize: i === 0 ? 14 : 12 }}>{l}</div>
        )) : <div style={cell}>—</div>}
        {result.recipient?.phoneNumber && <div style={cell}>{result.recipient.phoneNumber}</div>}
      </div>

      {/* Service band — the simulator uses the short marketing name
          ("PRIORITY OVERNIGHT"), not the full "FedEx …®" string. */}
      <div style={{ background: ORANGE, color: "#fff", fontWeight: 800, textAlign: "center", padding: "8px 10px", margin: "10px 0", letterSpacing: ".06em", fontSize: 15 }}>
        {shortServiceName(result.serviceName || result.serviceType)}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, paddingBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="qw-eyebrow">Service</div>
          <div style={{ ...cell, fontWeight: 700 }}>{result.serviceName || result.serviceType || "—"}</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div className="qw-eyebrow">Billing</div>
          <div style={{ ...cell, fontWeight: 700 }}>{billing || "—"}</div>
        </div>
      </div>

      {/* Tracking bar — ★★ flanking, as the simulator draws it. */}
      <div style={{ background: PURPLE, color: "#fff", fontWeight: 700, textAlign: "center", padding: "9px 10px", letterSpacing: ".06em", fontSize: 14 }}>
        <span className="qw-mono">★★ TRK# {result.trackingNumber || "—"} ★★</span>
      </div>

      {/* Scannable Code 128 of the tracking number, plus the human-readable
          form grouped in fours — same arrangement as the printed label.
          This encodes the tracking number only; it is a lookup aid, not a
          substitute for the FedEx label the parcel actually ships with. */}
      {result.trackingNumber && (
        <div style={{ padding: "10px 0 2px" }}>
          <Barcode128 value={result.trackingNumber} />
          <div className="qw-mono" style={{ textAlign: "center", fontSize: 15, letterSpacing: ".22em", marginTop: 6, color: "#0F172A" }}>
            {groupDigits(result.trackingNumber)}
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, borderTop: "1px solid #0F172A", marginTop: 10, paddingTop: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div className="qw-eyebrow">Reference</div>
          <div style={{ ...cell, fontWeight: 700 }}>{result.customerReference || `Order ${orderNum}`}</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div className="qw-eyebrow">Freighted ship via</div>
          <div className="qw-mono" style={{ ...cell, fontWeight: 700 }}>{result.freightedShipViaCode || "—"}</div>
        </div>
      </div>
    </div>
  );
}

// ── Code 128 barcode ─────────────────────────────────────────────────────────
// A REAL Code 128 encoding of the tracking number, rendered as SVG — not a
// decorative pattern. Anything that looks like a barcode will eventually be
// pointed at a scanner, and bars that decode to nothing (or to the wrong
// number) are worse than no barcode at all.
//
// Each symbol is 11 modules described by 6 alternating bar/space widths; the
// Stop symbol is 13 modules over 7 widths. Checksum is
// (start + Σ position×value) mod 103.
const CODE128_PATTERNS = [
  "212222","222122","222221","121223","121322","131222","122213","122312","132212","221213",
  "221312","231212","112232","122132","122231","113222","123122","123221","223211","221132",
  "221231","213212","223112","312131","311222","321122","321221","312212","322112","322211",
  "212123","212321","232121","111323","131123","131321","112313","132113","132311","211313",
  "231113","231311","112133","112331","132131","113123","113321","133121","313121","211331",
  "231131","213113","213311","213131","311123","311321","331121","312113","312311","332111",
  "314111","221411","431111","111224","111422","121124","121421","141122","141221","112214",
  "112412","122114","122411","142112","142211","241211","221114","413111","241112","134111",
  "111242","121142","121241","114212","124112","124211","411212","421112","421211","212141",
  "214121","412121","111143","111341","131141","114113","114311","411113","411311","113141",
  "114131","311141","411131","211412","211214","211232","2331112",
];
const CODE128_START_B = 104;
const CODE128_START_C = 105;
const CODE128_STOP = 106;

/// Returns the symbol values for `text`, or null when it can't be encoded.
function code128Values(text) {
  const s = String(text || "").trim();
  if (!s) return null;
  const digitsOnly = /^\d+$/.test(s);

  let values;
  if (digitsOnly && s.length % 2 === 0) {
    // Code 128C — two digits per symbol, the compact form used on parcel labels.
    values = [CODE128_START_C];
    for (let i = 0; i < s.length; i += 2) values.push(Number(s.slice(i, i + 2)));
  } else {
    // Code 128B — one character per symbol. Handles odd-length numbers (FedEx
    // also issues 15-digit tracking) and anything non-numeric.
    values = [CODE128_START_B];
    for (const ch of s) {
      const v = ch.charCodeAt(0) - 32;
      if (v < 0 || v > 94) return null;   // outside Code 128B's range
      values.push(v);
    }
  }

  let sum = values[0];
  for (let i = 1; i < values.length; i++) sum += i * values[i];
  values.push(sum % 103);
  values.push(CODE128_STOP);
  return values;
}

/// Scannable Code 128 barcode. Renders nothing if the value can't be encoded.
function Barcode128({ value, height = 56, moduleWidth = 2 }) {
  const values = code128Values(value);
  if (!values) return null;

  const bars = [];
  let x = 0;
  for (const v of values) {
    const widths = CODE128_PATTERNS[v];
    let isBar = true;                       // every symbol starts with a bar
    for (const w of widths) {
      const units = Number(w);
      if (isBar) bars.push({ x, w: units * moduleWidth });
      x += units * moduleWidth;
      isBar = !isBar;
    }
  }
  const quiet = 10 * moduleWidth;           // Code 128 needs a 10-module quiet zone
  const total = x + quiet * 2;

  return (
    <svg width="100%" viewBox={`0 0 ${total} ${height}`} preserveAspectRatio="none"
      role="img" aria-label={`Barcode for tracking number ${value}`}
      style={{ display: "block" }}>
      <rect x="0" y="0" width={total} height={height} fill="#fff" />
      {bars.map((b, i) => (
        <rect key={i} x={b.x + quiet} y="0" width={b.w} height={height} fill="#0F172A" />
      ))}
    </svg>
  );
}

// "FedEx Priority Overnight®" -> "PRIORITY OVERNIGHT", matching the band on a
// real label. Falls back to the raw value uppercased for anything unexpected.
function shortServiceName(name) {
  return String(name || "")
    .replace(/®|™/g, "")
    .replace(/^FedEx\s+/i, "")
    .trim()
    .toUpperCase() || "—";
}

// 794843196680 -> "7948 4319 6680"
function groupDigits(s) {
  const d = String(s || "").replace(/\D/g, "");
  return d ? d.replace(/(\d{4})(?=\d)/g, "$1 ") : "";
}

// ── Readiness tiles ──────────────────────────────────────────────────────────
// The simulator's three pre-flight cards: Shipper / Recipient / Package. Green
// when the section is satisfied, amber when it needs the clerk. `source` is the
// readiness response's ShipperSource / RecipientSource (e.g. "Plant:MfgSys") —
// when an address looks wrong, that's what tells the clerk where it came from.
// ── FedEx address verdict ────────────────────────────────────────────────────
// The validate endpoint returns the resolved address, a business/residential
// classification, DPV confirmation and per-field notices. The UI used to print
// one line — "⚠ FedEx: RESIDENTIAL · isValid=false · 2 message(s)" — so the
// clerk was told there were two messages but never what they said, and the
// corrected address FedEx handed back was thrown away. All of it is shown here.
function AddressVerdict({ state, onApply }) {
  if (!state) {
    return <div style={{ fontSize: 12, color: "#94A3B8" }}>📡 Checked against FedEx automatically, and again whenever you edit the address.</div>;
  }
  if (state.busy) return <div style={{ fontSize: 12, color: "#7C3AED" }}>⏳ Validating with FedEx…</div>;
  if (state.error) {
    // The CHECK did not run — a neutral note, never the red "FedEx wouldn't accept the
    // delivery address" (UI-SHP-041). That sentence came from the message humanizer seeing
    // the word "address" in "address validation error", so a missing FedEx secret blamed a
    // good address. The server's own reason is shown as-is, small, for IT.
    const reason = String(state.error).replace(/^Couldn't check this address with FedEx:\s*/i, "");
    return (
      <div style={{ fontSize: 12, minWidth: 0, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "8px 10px" }}>
        <div style={{ fontWeight: 600, color: "#334155" }}>
          Couldn't check this address with FedEx right now — you can still continue.
        </div>
        <div style={{ color: "#94A3B8", marginTop: 3, overflowWrap: "anywhere" }}>Reason: {reason}</div>
      </div>
    );
  }
  const v = state.result;
  if (!v) return null;

  const ok = !!v.isValid;
  const tone = ok
    ? { bg: "#F0FDF4", border: "#BBF7D0", head: "#14532D", body: "#166534" }
    : { bg: "#FFFBEB", border: "#FDE68A", head: "#78350F", body: "#92400E" };
  const ra = v.resolvedAddress || null;
  const d = v.diagnostics || {};
  const resolvedLine = ra
    ? [ (ra.streetLines || []).filter(Boolean).join(", "),
        [ra.city, ra.stateOrProvinceCode].filter(Boolean).join(", "),
        ra.postalCode, ra.countryCode ].filter(Boolean).join(" · ")
    : null;

  return (
    <div style={{ borderRadius: 12, padding: 12, background: tone.bg, border: `1px solid ${tone.border}`, fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 16, lineHeight: 1, color: tone.body }}>{ok ? "✓" : "!"}</span>
        <span style={{ fontWeight: 700, color: tone.head }}>
          {ok ? "FedEx verified this address" : "FedEx could not fully verify this address"}
        </span>
        {v.classification && <span className="qw-pill qw-pill-slate">{v.classification}</span>}
        {v.isPoBox && <span className="qw-pill" style={{ background: "#FEE2E2", color: "#991B1B" }}>PO BOX</span>}
      </div>

      {/* Only worth showing when FedEx actually rewrote something. */}
      {v.inputChanged && resolvedLine && (
        <div style={{ marginTop: 8, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 10px" }}>
          <div style={{ color: "#64748B", marginBottom: 2 }}>FedEx suggests:</div>
          <div style={{ fontWeight: 600, color: "#0F172A" }}>{resolvedLine}</div>
          {onApply && (
            <button style={{ ...linkBtn, color: "#4D148C", marginTop: 6 }} onClick={onApply}>
              Use FedEx's version →
            </button>
          )}
        </div>
      )}

      {(d.suiteRequiredButMissing || d.invalidSuiteNumber || d.dpvConfirmed === false) && (
        <ul style={{ margin: "8px 0 0 18px", padding: 0, color: tone.body }}>
          {d.suiteRequiredButMissing && <li>This building needs a suite/unit number — none was given.</li>}
          {d.invalidSuiteNumber && <li>The suite/unit number doesn't match this building.</li>}
          {d.dpvConfirmed === false && <li>Not confirmed as a deliverable address by USPS.</li>}
        </ul>
      )}

      {(v.messages || []).length > 0 && (
        <ul style={{ margin: "8px 0 0 18px", padding: 0, color: tone.body }}>
          {v.messages.map((m, i) => <li key={m.code || i}>{m.message || m.code}</li>)}
        </ul>
      )}

      {!ok && (
        <div style={{ color: "#94A3B8", marginTop: 8 }}>
          You can still buy a label — FedEx flags plenty of real addresses — but a genuine
          mistake here surfaces as a failed label or a misdelivery.
        </div>
      )}
    </div>
  );
}

function ReadinessTile({ ok, title, detail, source, children }) {
  const tone = ok
    ? { bg: "#F0FDF4", border: "#BBF7D0", head: "#14532D", body: "#166534" }
    : { bg: "#FFFBEB", border: "#FDE68A", head: "#78350F", body: "#92400E" };
  return (
    <div style={{ borderRadius: 12, padding: 14, background: tone.bg, border: `1px solid ${tone.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 20, lineHeight: 1, color: tone.body }}>{ok ? "✓" : "!"}</span>
        <span style={{ fontWeight: 600, color: tone.head }}>{title}</span>
      </div>
      <div style={{ fontSize: 12, color: tone.body, marginTop: 4 }}>{detail}</div>
      {source && (
        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>
          Source: <span className="qw-mono">{source}</span>
        </div>
      )}
      {children && <div style={{ marginTop: 8, display: "flex", gap: 12, flexWrap: "wrap" }}>{children}</div>}
    </div>
  );
}

// ── Small presentational bits ────────────────────────────────────────────────
function StagePills({ stage }) {
  const labels = ["Order", "Lines", "Review", "Service", "Done"];
  // `se-stagepills` drives a phone-only restyle (see DynamicQw.css). The rail is
  // `overflow-x: auto`, so on a 440px screen stages 4 and 5 were simply scrolled
  // off the right edge (measured: "5 · Done" 233px past the viewport) — a rep
  // could not see how many steps were left or where they were past Review.
  return (
    <div className="se-stagepills" style={{ display: "flex", alignItems: "center", gap: 6, overflowX: "auto", paddingBottom: 8, marginBottom: 18 }}>
      {labels.map((lbl, i) => {
        const n = i + 1;
        const state = n < stage ? "done" : n === stage ? "active" : "todo";
        const bg = state === "active" ? PURPLE : state === "done" ? "#F4F4F5" : "#F4F4F5";
        const color = state === "active" ? "#fff" : state === "done" ? PURPLE : "#9CA3AF";
        return (
          <React.Fragment key={lbl}>
            <span style={{
              background: bg, color, padding: "6px 14px", borderRadius: 999, fontSize: 13,
              fontWeight: 600, whiteSpace: "nowrap",
              // The active pill lifts off the rail exactly as the simulator does.
              transform: state === "active" ? "scale(1.05)" : "none",
              boxShadow: state === "active" ? "0 4px 12px rgba(77,20,140,.35)" : "none",
              transition: "all .3s ease",
            }}>
              {n} · {lbl}
            </span>
            {n < labels.length && <span style={{ color: "#D4D4D8" }}>›</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// =============================================================================
// Main component
// =============================================================================
export default function ShipmentEntry() {
  const { orderNum } = useParams();
  const navigate = useNavigate();

  const [stage, setStage] = useState(1);
  const [banner, setBanner] = useState(null);       // { type, text }

  // ── Stage 1: order ──────────────────────────────────────────────────────
  const [orderLoading, setOrderLoading] = useState(true);
  const [orderErr, setOrderErr] = useState(null);
  const [order, setOrder] = useState(null);

  const header = order?.header || {};
  const shippableRows = useMemo(() => buildShippableRows(order), [order]);

  // Up-front "can this order even be shipped?" gate — see utils/shipGate.
  const shipBlockReason = useMemo(() => computeShipBlockReason(order, "long"), [order]);

  // ── Open packs already on this order ────────────────────────────────────
  // Creating a pack is unconditional on the server, and packNum only lives in
  // this component's state — so closing the tab and coming back, or a second
  // clerk opening the same order, silently created a duplicate pack and
  // orphaned the first. Look for one that's still open before offering to
  // make another.
  //
  // linkedShipments (from ?include=shipments) is THREE-valued and the
  // difference is load-bearing — see SalesOrderFullDTO:
  //   [rows] → these packs reference the order
  //   []     → the order genuinely has none
  //   null   → the lookup FAILED. Unknown, never "none".
  // Collapsing null to [] is exactly how you'd recreate the duplicate-pack
  // bug on the day Epicor is slow, so it's surfaced instead of assumed.
  const linkedShipments = order ? (order.linkedShipments ?? null) : null;
  const shipmentsUnknown = !!order && linkedShipments === null;
  const openPacks = useMemo(() => (linkedShipments || []).filter((s) => {
    const status = String(s.shipStatus || "").trim().toUpperCase();
    return !s.voided && !s.invoiced && status !== "SHIPPED";
  }), [linkedShipments]);

  // How many lines each open pack holds. linkedShipments doesn't carry a line
  // count, and "is this pack empty?" is the whole question — an open pack with
  // no lines isn't a shipment in progress, it's a leftover from someone
  // starting the flow and not finishing. One GET per open pack; there are
  // rarely more than a couple.
  const [packLines, setPackLines] = useState(null); // { [packNum]: count|null }
  useEffect(() => {
    if (!openPacks.length) { setPackLines(null); return undefined; }
    let alive = true;
    (async () => {
      const entries = await Promise.all(openPacks.map(async (p) => {
        try {
          const r = await apiFetch(`${API_URL}/api/shipment-entry/${p.packNum}`);
          const d = await r.json().catch(() => ({}));
          if (!r.ok || d.success === false) return [p.packNum, null];
          return [p.packNum, (d.lines || []).length];
        } catch { return [p.packNum, null]; }
      }));
      if (alive) setPackLines(Object.fromEntries(entries));
    })();
    return () => { alive = false; };
  }, [openPacks]);

  // The empty open pack we'll reuse instead of creating another.
  //
  // Oldest first, so the orphans that have already piled up get consumed
  // before any new pack is minted. A null count means the lookup failed —
  // that is "don't know", never "empty", because adopting a pack that
  // actually holds lines would silently add this order's lines to it.
  const reusablePack = useMemo(() => {
    if (!packLines) return null;
    return openPacks
      .filter((p) => packLines[p.packNum] === 0)
      .sort((a, b) => a.packNum - b.packNum)[0] || null;
  }, [openPacks, packLines]);

  const loadOrder = useCallback(async () => {
    setOrderLoading(true); setOrderErr(null);
    try {
      const r = await apiFetch(`${API_URL}/api/sales-orders/${encodeURIComponent(orderNum)}?include=shipments`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false || !data.order) {
        throw new Error(data.error || `Order ${orderNum} could not be loaded (HTTP ${r.status}).`);
      }
      setOrder(data.order);
    } catch (e) {
      setOrderErr(e.message || "Failed to load order.");
    } finally {
      setOrderLoading(false);
    }
  }, [orderNum]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  // ── Stage 2: line selection + pickers + pack creation ───────────────────
  // Pack-creation constants. These were editable fields on Stage 2; none of
  // them is a per-shipment decision, so the inputs were removed and the values
  // are sent from here. Promote back to state if a real need to vary appears.
  //
  // Epicor requires Division (Character03) to create a pack and it has no
  // source on the order or any endpoint. Environment-configurable rather than
  // buried here, so a second division doesn't need a code change.
  const division = SHIP_DIVISION;
  // Some order Ship Via codes (e.g. freight FED1) carry an Epicor BPM that
  // demands a tracking number the instant the pack is created — before the
  // FedEx label (and its tracking) exists. "BPO" is BPM-exempt, so the pack is
  // always created under it and the label-buy stamps the real freighted
  // carrier + tracking afterward. Changing this re-introduces that failure.
  const shipViaOverride = SHIP_VIA_CREATE_CODE;
  // NOTE: pack weight is deliberately NOT set at creation. It is a physical
  // measurement no API can supply, and stamping a placeholder made the pack
  // read as "package ready" while carrying a number nobody measured. The pack
  // is created weightless; readiness then reports weight as missing and the
  // clerk enters the real value on Stage 3.
  const [picks, setPicks] = useState({});           // key -> { checked, qty, bin, lot, bins, lots, loadingBins, loadingLots }
  const [creating, setCreating] = useState(false);
  const [resuming, setResuming] = useState(false);   // loading an existing open pack
  const [packAdopted, setPackAdopted] = useState(false); // packNum came from an existing empty pack
  const [packNum, setPackNum] = useState(null);      // set once the pack is created (or resumed)
  const [createdLines, setCreatedLines] = useState([]); // committed line summaries for the review screen

  // Seed pick state once the shippable rows resolve.
  useEffect(() => {
    if (!shippableRows.length) return;
    setPicks((prev) => {
      const next = { ...prev };
      for (const row of shippableRows) {
        if (!next[row.key]) {
          next[row.key] = {
            checked: true, qty: row.remaining, bin: "", lot: "",
            bins: null, lots: null, loadingBins: false, loadingLots: false,
          };
        }
      }
      return next;
    });
  }, [shippableRows]);

  const patchPick = useCallback((key, delta) => {
    setPicks((prev) => ({ ...prev, [key]: { ...prev[key], ...delta } }));
  }, []);

  // Live mirror of `picks` for async loops (auto-select awaits bin fetches per
  // line; reading the state captured at click time would see none of them land).
  const picksRef = useRef(picks);
  useEffect(() => { picksRef.current = picks; }, [picks]);

  // Build the bin↔lot matrix when Epicor won't return it in one call.
  //
  // Epicor honours `LotNumber = 'X'` (that lookup is what proved bin S1A3 holds
  // none of lot 0095214) but appears to aggregate when the clause is dropped.
  // So: list the part's lots once, then ask per lot which bins hold it, and
  // invert the answers into lots-keyed-by-bin — the shape the simulator models
  // and the shape that makes an impossible pairing unselectable.
  //
  // Runs AFTER the bins are already on screen, so the clerk isn't kept waiting;
  // the lot dropdown tightens as soon as the answers land.
  const buildLotMatrix = useCallback(async (row, bins) => {
    // A part can carry a long lot history and this is one call per lot. Cap it,
    // and say so rather than silently narrowing the picker on a partial matrix.
    const MAX_LOTS = 25;
    try {
      // Blank bin = every lot for the part in this warehouse, which is what the
      // matrix has to start from. (The bin-scoped form of this call IS scoped —
      // Epicor honours ipBinNum. It was long commented here as the bug behind
      // all this; the picker logs disprove that, so don't restore the claim.)
      const lotsUrl = `${API_URL}/api/shipment-entry/pickers/lots?part=${encodeURIComponent(row.partNum)}`
        + `&warehouse=${encodeURIComponent(row.warehouseCode)}&bin=&onHandOnly=true`;
      const lr = await apiFetch(lotsUrl);
      const ldata = await lr.json().catch(() => ({}));
      const allLots = (lr.ok && ldata.success !== false && Array.isArray(ldata.lots)) ? ldata.lots : [];
      if (!allLots.length) return;               // nothing learned — leave the old behaviour in place

      const lots = allLots.slice(0, MAX_LOTS);
      const results = await Promise.all(lots.map(async (l) => {
        try {
          // onHandOnly=true for the same paging reason as loadLotInBin below:
          // the unscoped form returns page 1 of every bin in the warehouse, all
          // empty, and the matrix comes out uniformly zero.
          const u = `${API_URL}/api/shipment-entry/pickers/bins?part=${encodeURIComponent(row.partNum)}`
            + `&warehouse=${encodeURIComponent(row.warehouseCode)}&lot=${encodeURIComponent(l.lotNum)}&onHandOnly=true`;
          const rr = await apiFetch(u);
          const d = await rr.json().catch(() => ({}));
          return { lot: l.lotNum, rows: (rr.ok && d.success !== false && Array.isArray(d.bins)) ? d.bins : null };
        } catch { return { lot: l.lotNum, rows: null }; }
      }));

      // If not one lot placed stock in any bin, the lookups told us nothing —
      // every one failed, or the filter isn't behaving as assumed. Constraining
      // the picker on that would block every shipment, so leave it alone.
      const learnedSomething = results.some((x) => (x.rows || []).some((b) => Number(b.onHandQty) > 0));
      if (!learnedSomething) return;

      const map = {};
      for (const b of bins) map[b.binNum] = [];
      for (const { lot, rows } of results) {
        for (const b of (rows || [])) {
          const q = Number(b.onHandQty) || 0;
          if (q <= 0) continue;                  // a zero row means "not here"
          if (!map[b.binNum]) map[b.binNum] = [];
          map[b.binNum].push({ lotNum: lot, onHandQty: q });
        }
      }

      // Re-sync a bin the clerk already picked: its lot list just changed, and a
      // lot that is no longer valid for it must not stay selected.
      setPicks((prev) => {
        const p = prev[row.key];
        if (!p) return prev;
        const next = { ...p, lotsByBin: map, lotMatrixTruncated: allLots.length > MAX_LOTS };
        if (p.bin) {
          const binLots = map[p.bin] || [];
          next.lots = binLots;
          if (!binLots.some((l) => l.lotNum === p.lot)) {
            next.lot = binLots.length === 1 ? binLots[0].lotNum : "";
          }
          next.lotInBin = null;
          next.lotInBinFor = null;
        }
        return { ...prev, [row.key]: next };
      });
    } catch {
      /* leave lotsByBin null — the guard falls back to the lot-scoped check */
    }
  }, []);

  const loadBins = useCallback(async (row) => {
    // The picker requires a warehouse (ListBinsHandler 400s without one). A
    // release with no warehouse used to render as "✓ 0 bins" — a success tick
    // over a hard error — leaving the clerk with nothing to pick and no reason.
    if (!row.warehouseCode) {
      patchPick(row.key, { bins: [], loadingBins: false, binsError: "No warehouse on this release — bins can't be listed." });
      return;
    }
    patchPick(row.key, { loadingBins: true, binsError: null });
    try {
      // Ask for the bin↔lot matrix (one row per bin+lot). The simulator models
      // the pickers this way — `lots` keyed BY BIN — and it's the only shape in
      // which the lot dropdown can be limited to lots the chosen bin actually
      // holds. The flat per-bin list let the clerk pair bin S1A3 with a lot
      // living elsewhere: both dropdowns showed 5, the intersection was 0, and
      // Epicor refused the line after the pack had already been created.
      const url = `${API_URL}/api/shipment-entry/pickers/bins?part=${encodeURIComponent(row.partNum)}`
        + `&warehouse=${encodeURIComponent(row.warehouseCode)}&perLot=true&onHandOnly=true`;
      const r = await apiFetch(url);
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        patchPick(row.key, { bins: [], loadingBins: false, binsError: data.error || `Bin lookup failed (HTTP ${r.status}).` });
        return;
      }
      const rows = Array.isArray(data.bins) ? data.bins : [];

      // data.perLot is the server's word on whether Epicor honoured the request.
      // When it didn't, the matrix still has to be built — the requirement is
      // that an impossible bin+lot pair can't be picked, and falling back to the
      // unscoped lot list doesn't meet it. Show the bins immediately (they're
      // correct either way) and assemble the matrix from lot-scoped calls, which
      // Epicor demonstrably does honour.
      if (!data.perLot) {
        patchPick(row.key, { bins: rows, lotsByBin: null, loadingBins: false, binsError: null });
        buildLotMatrix(row, rows);
        return;
      }

      // Collapse the matrix into the two structures the UI needs: one entry per
      // bin (qty = the sum of its lots) and, per bin, the lots really in it.
      const byBin = new Map();
      for (const m of rows) {
        const qty = Number(m.onHandQty) || 0;
        let entry = byBin.get(m.binNum);
        if (!entry) {
          entry = { binNum: m.binNum, binDesc: m.binDesc, onHandQty: 0, allocatedQty: 0, uom: m.uom, nonNettable: m.nonNettable, binType: m.binType, lots: [] };
          byBin.set(m.binNum, entry);
        }
        entry.onHandQty += qty;
        entry.allocatedQty += Number(m.allocatedQty) || 0;
        if (m.lotNumber) entry.lots.push({ lotNum: m.lotNumber, onHandQty: qty });
      }
      const bins = [...byBin.values()];
      const lotsByBin = {};
      for (const b of bins) lotsByBin[b.binNum] = b.lots;

      patchPick(row.key, { bins, lotsByBin, loadingBins: false, binsError: null });
    } catch (e) {
      patchPick(row.key, { bins: [], loadingBins: false, binsError: e.message || "Bin lookup failed." });
    }
  }, [patchPick, buildLotMatrix]);

  const loadLots = useCallback(async (row, bin, lotsByBin) => {
    if (!bin) { patchPick(row.key, { lots: null, lot: "", lotInBin: null, lotInBinFor: null }); return; }

    // Matrix already in hand — the lots for this bin are known, no call needed.
    // This is what makes an impossible bin+lot pair unselectable: the dropdown
    // is built from the lots this bin holds, not from every lot of the part.
    if (lotsByBin) {
      const lots = lotsByBin[bin] || [];
      const auto = lots.length === 1 ? lots[0].lotNum : "";
      patchPick(row.key, { lots, lot: auto, loadingLots: false, lotInBin: null, lotInBinFor: null });
      return;
    }

    patchPick(row.key, { loadingLots: true, lot: "", lotInBin: null, lotInBinFor: null });
    try {
      const url = `${API_URL}/api/shipment-entry/pickers/lots?part=${encodeURIComponent(row.partNum)}`
        + `&warehouse=${encodeURIComponent(row.warehouseCode)}&bin=${encodeURIComponent(bin)}&onHandOnly=true`;
      const r = await apiFetch(url);
      const data = await r.json().catch(() => ({}));
      const lots = (r.ok && data.success !== false && Array.isArray(data.lots)) ? data.lots : [];
      // Auto-select the only lot in a bin — matches real warehouse behaviour.
      const auto = lots.length === 1 ? lots[0].lotNum : "";
      patchPick(row.key, { lots, lot: auto, loadingLots: false });
    } catch {
      patchPick(row.key, { lots: [], loadingLots: false });
    }
  }, [patchPick]);

  // The number Epicor actually checks: how much of THIS lot sits in THIS bin.
  //
  // Neither figure already on screen answers that. The bin row totals every lot
  // in the bin; the lot row totals that lot across every bin. Both can read 5
  // while their intersection is 0 — which is how a qty-1 shipment gets refused
  // with "negative onhand quantity for the bin" against a bin showing 5. Asking
  // the bin picker for a single lot returns exactly the intersection.
  const loadLotInBin = useCallback(async (row, bin, lot) => {
    if (!bin || !lot || !row.warehouseCode) { patchPick(row.key, { lotInBin: null }); return; }
    patchPick(row.key, { loadingLotInBin: true });
    try {
      const url = `${API_URL}/api/shipment-entry/pickers/bins?part=${encodeURIComponent(row.partNum)}`
        + `&warehouse=${encodeURIComponent(row.warehouseCode)}&lot=${encodeURIComponent(lot)}`
        // On-hand only. Asking for every bin instead (onHandOnly=false) sounds
        // safer — a missing row and a zero row would then mean the same thing —
        // but the response is PAGED at 50 and ordered by bin, so the warehouse's
        // empty bins fill page 1 and the bin actually being picked never appears.
        // "Not on page 1" then scored as zero, and a valid pick was refused with
        // "bin holds none of this lot". Scoped to on-hand, the lot lives in a
        // handful of bins, they all fit on one page, and absent really is empty.
        + `&onHandOnly=true`;
      const r = await apiFetch(url);
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false || !Array.isArray(data.bins)) {
        // Leave it null rather than guessing: the guard below then falls back to
        // the old (looser) check instead of blocking a legitimate shipment on a
        // failed lookup.
        patchPick(row.key, { lotInBin: null, loadingLotInBin: false });
        return;
      }
      const match = data.bins.find((b) => b.binNum === bin);
      patchPick(row.key, {
        lotInBin: match ? Number(match.onHandQty) : 0,
        lotInBinAttrSet: match ? match.attributeSetID : null,
        loadingLotInBin: false,
      });
    } catch {
      patchPick(row.key, { lotInBin: null, loadingLotInBin: false });
    }
  }, [patchPick]);

  // ── Auto select: one click fills bin + lot for every still-blank line ──────
  //
  // Picking rules (agreed with the user):
  //   • blank lines only — a bin the clerk already chose is never overwritten;
  //   • rental stock is excluded: any bin or lot whose name/description
  //     contains "rental", plus bins Epicor flags non-nettable (the flag the
  //     picker carries specifically to distinguish rental/quarantine bins);
  //   • only pairs whose bin∩lot quantity covers the line's ship qty — the one
  //     figure Epicor actually validates (see lotInBinQty);
  //   • FEFO: among eligible pairs, the lot expiring first wins (lots without
  //     an expiration date go last); ties keep Epicor's bin order.
  // A line is one bin+lot (no split), so a line larger than any single pair
  // can't be auto-filled — it gets a note and stays manual.
  const RENTAL_RE = /rental/i;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoProgress, setAutoProgress] = useState(null);   // "3 / 12"

  // Part-wide lot list (blank bin = unscoped) — the only source of expiration
  // dates, which the bin↔lot matrix rows don't carry.
  const fetchLotExpirations = async (row) => {
    try {
      const url = `${API_URL}/api/shipment-entry/pickers/lots?part=${encodeURIComponent(row.partNum)}`
        + `&warehouse=${encodeURIComponent(row.warehouseCode)}&bin=&onHandOnly=true`;
      const r = await apiFetch(url);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false || !Array.isArray(d.lots)) return {};
      const map = {};
      for (const l of d.lots) if (l.lotNum) map[l.lotNum] = l.expirationDate || null;
      return map;
    } catch { return {}; }
  };

  const autoSelectRow = async (row, expCache) => {
    // The bin↔lot matrix loads per line in the background; kick it off if it
    // hasn't, then wait for it (bounded — the fallback path is many calls).
    let p = picksRef.current[row.key] || {};
    if (p.bins == null && !p.loadingBins) loadBins(row);
    const deadline = Date.now() + 30000;
    for (;;) {
      p = picksRef.current[row.key] || {};
      if (p.binsError || (p.bins != null && (p.lotsByBin || p.bins.length === 0))) break;
      if (Date.now() > deadline) break;
      await sleep(250);
    }
    if (p.binsError || !p.bins || p.bins.length === 0) {
      patchPick(row.key, { autoNote: "No bins to pick from — choose manually." });
      return;
    }
    if (!p.lotsByBin) {
      patchPick(row.key, { autoNote: "Lot availability didn't load in time — pick manually." });
      return;
    }

    const qtyNeeded = Number(p.qty) > 0 ? Number(p.qty) : Number(row.remaining);
    const cacheKey = `${row.partNum}|${row.warehouseCode}`;
    if (!(cacheKey in expCache)) expCache[cacheKey] = await fetchLotExpirations(row);
    const expOf = expCache[cacheKey];

    const candidates = [];
    for (const b of p.bins) {
      if (RENTAL_RE.test(b.binNum || "") || RENTAL_RE.test(b.binDesc || "") || b.nonNettable === true) continue;
      const lots = p.lotsByBin[b.binNum] || [];
      if (lots.length === 0) {
        // Non-lot part: the bin total IS the shippable figure.
        if (Number(b.onHandQty) >= qtyNeeded) candidates.push({ bin: b.binNum, lot: "", exp: null });
        continue;
      }
      for (const l of lots) {
        if (RENTAL_RE.test(l.lotNum || "")) continue;
        if (Number(l.onHandQty) >= qtyNeeded)
          candidates.push({ bin: b.binNum, lot: l.lotNum, exp: expOf[l.lotNum] || null });
      }
    }
    if (!candidates.length) {
      patchPick(row.key, {
        autoNote: `No non-rental bin holds ${fmtQty(qtyNeeded)} of one lot — pick manually`
          + ` (a line can't be split across bins).`,
      });
      return;
    }

    candidates.sort((a, b) =>
      (a.exp ? Date.parse(a.exp) : Infinity) - (b.exp ? Date.parse(b.exp) : Infinity));
    const chosen = candidates[0];
    // Mirror the manual flow's state exactly: bin set, lot list scoped to the
    // bin (matrix mode, so pickProblem reads the true intersection), lot set.
    patchPick(row.key, {
      bin: chosen.bin, lots: p.lotsByBin[chosen.bin] || [], lot: chosen.lot,
      loadingLots: false, lotInBin: null, lotInBinFor: null, autoNote: null,
    });
  };

  const autoSelectAll = async () => {
    setAutoBusy(true);
    const expCache = {};
    try {
      const targets = shippableRows.filter((row) => {
        const p = picksRef.current[row.key] || {};
        return p.checked && !p.bin && !createdLines.some((c) => sameLine(c, row));
      });
      let done = 0;
      // Sequential on purpose: each line may still be loading its matrix, and
      // the fallback path costs many Epicor calls per line.
      for (const row of targets) {
        setAutoProgress(`${++done} / ${targets.length}`);
        await autoSelectRow(row, expCache);
      }
    } finally { setAutoBusy(false); setAutoProgress(null); }
  };

  // Resolve the lot-in-bin figure whenever a bin+lot pair settles (including the
  // auto-selected single lot above).
  useEffect(() => {
    if (stage !== 2) return;
    for (const row of shippableRows) {
      const p = picks[row.key];
      if (!p || !p.checked || !p.bin || !p.lot) continue;
      // In matrix mode the lot rows ARE per-bin quantities, so the intersection
      // is already known and this extra round-trip would re-fetch what we have.
      if (p.lotsByBin) continue;
      // Keyed on the bin+lot pair alone. Keying on the RESULT as well would
      // re-fire forever, since the pending state is itself "no result yet".
      const pairKey = `${p.bin}|${p.lot}`;
      if (p.lotInBinFor !== pairKey) {
        patchPick(row.key, { lotInBinFor: pairKey });
        loadLotInBin(row, p.bin, p.lot);
      }
    }
  }, [stage, shippableRows, picks, loadLotInBin, patchPick]);

  // On entering Stage 2, fire the bin picker for each checked line.
  useEffect(() => {
    if (stage !== 2) return;
    for (const row of shippableRows) {
      const p = picks[row.key];
      if (p && p.checked && p.bins == null && !p.loadingBins) loadBins(row);
    }
  }, [stage, shippableRows, picks, loadBins]);

  // How much of the picked LOT sits in the picked BIN — the only quantity Epicor
  // checks — or null when it isn't known yet. Two sources, same meaning: in
  // matrix mode the lot rows are already per-bin, otherwise it comes from the
  // lot-scoped bin lookup.
  function lotInBinQty(p) {
    if (!p || !p.bin || !p.lot) return null;
    if (p.lotsByBin) {
      const hit = (p.lots || []).find((l) => l.lotNum === p.lot);
      return hit ? Number(hit.onHandQty) : 0;
    }
    return typeof p.lotInBin === "number" && Number.isFinite(p.lotInBin) ? p.lotInBin : null;
  }

  // Why a line can't be shipped as picked, or null when it's fine.
  //
  // The on-hand check matters: Epicor rejects a ShipDtl that would drive a bin
  // (or lot) negative with "This transaction will result in a negative onhand
  // quantity for the bin." The picker already returns onHandQty per bin and per
  // lot, so there is no reason to spend a round-trip — and a half-created pack —
  // discovering that. Catch it while the clerk is still looking at the numbers.
  const pickProblem = useCallback((row) => {
    const p = picks[row.key] || {};
    if (!p.checked) return null;
    const qty = Number(p.qty);
    if (!(qty > 0)) return "Enter a ship qty greater than 0.";
    if (!p.bin) return "Pick a bin.";
    const needsLot = (p.lots && p.lots.length > 0);
    if (needsLot && !p.lot) return "Pick a lot.";

    // Prefer the lot-in-bin figure — the only one Epicor actually checks.
    //
    // This used to constrain against the LOT row, described as "the narrower
    // bucket". It isn't: the lot row totals that lot across every bin, so it is
    // WIDER than the bin, and the guard waved through quantities Epicor then
    // refused. Fall back to the old figures only while the precise lookup is
    // pending or has failed — a lookup failure must not block a shipment that
    // would have succeeded.
    const lotRow = p.lot ? (p.lots || []).find((l) => l.lotNum === p.lot) : null;
    const binRow = (p.bins || []).find((b) => b.binNum === p.bin);
    const exact = lotInBinQty(p);
    const precise = exact != null;
    const avail = precise
      ? exact
      : lotRow ? Number(lotRow.onHandQty)
      : binRow ? Number(binRow.onHandQty)
      : null;

    if (precise && exact <= 0) {
      return `Bin ${p.bin} holds none of lot ${p.lot}`
        + ` — Epicor will reject this line. Pick the bin that actually holds this lot,`
        + ` or a different lot in this bin.`;
    }
    if (avail != null && Number.isFinite(avail) && qty > avail) {
      const where = precise ? `lot ${p.lot} in bin ${p.bin}` : lotRow ? `lot ${p.lot}` : `bin ${p.bin}`;
      return `Only ${fmtQty(avail)} on hand in ${where}`
        + ` — shipping ${fmtQty(qty)} would drive it negative and Epicor will reject the line.`;
    }
    if (qty > Number(row.remaining)) {
      return `Ship qty exceeds the ${fmtQty(row.remaining)} still open on this line.`;
    }
    return null;
  }, [picks]);

  const stage2Ready = useMemo(() => {
    const checked = shippableRows.filter((r) => picks[r.key]?.checked);
    if (!checked.length) return false;
    return checked.every((r) => !pickProblem(r));
  }, [shippableRows, picks, pickProblem]);

  // Checked lines not yet accepted by Epicor. Non-zero after a partial failure.
  const pendingLineCount = useMemo(() => shippableRows.filter((r) =>
    picks[r.key]?.checked && !createdLines.some((c) => sameLine(c, r))
  ).length, [shippableRows, picks, createdLines]);

  async function createPackAndLines() {
    setCreating(true); setBanner(null);
    try {
      // 1) Get a pack header to add lines to, in order of preference:
      //
      //    a) one this session already made — line adds are the step that
      //       fails (bad bin, insufficient on-hand) and they fail AFTER the
      //       header exists, so a retry resumes into it rather than orphaning
      //       it behind a second one;
      //    b) an EXISTING open pack on this order that has no lines — it is a
      //       leftover from an unfinished attempt, and creating a second empty
      //       pack beside it is pure waste. Epicor ties it to this order via
      //       ShipHead.OTSOrderNum, so adopting it is safe;
      //    c) failing both, create one.
      let newPack = packNum;
      let adopted = false;
      if (!newPack && reusablePack) {
        newPack = reusablePack.packNum;
        adopted = true;
        setPackAdopted(true);
      }
      if (!newPack) {
        const createRes = await apiFetch(`${API_URL}/api/shipment-entry`, {
          method: "POST",
          body: JSON.stringify({
            orderNum: String(orderNum),          // CreatePack.orderNum is a string
            division,
            comments: "",
            ...(shipViaOverride.trim() ? { shipViaCodeOverride: shipViaOverride.trim() } : {}),
          }),
        });
        const createData = await createRes.json().catch(() => ({}));
        if (!createRes.ok || createData.success === false || !createData.data?.packNum) {
          throw new Error(createData.failedReason || createData.message || `Pack creation failed (HTTP ${createRes.status}).`);
        }
        newPack = createData.data.packNum;
      }

      // 2) Add each checked line that isn't on the pack yet. Stop on the first
      //    failure so the clerk can fix it — already-committed lines are kept
      //    so a retry never double-adds them.
      const committed = [...createdLines];
      const isCommitted = (r) => committed.some((c) => sameLine(c, r));
      const checked = shippableRows.filter((r) => picks[r.key]?.checked && !isCommitted(r));
      for (const row of checked) {
        const p = picks[row.key];
        const lineRes = await apiFetch(`${API_URL}/api/shipment-entry/${newPack}/lines`, {
          method: "POST",
          body: JSON.stringify({
            orderNum: Number(orderNum),        // AddLine.orderNum is a number
            orderLine: row.orderLine,
            orderRelNum: row.orderRelNum,
            binNum: p.bin,
            lotNum: p.lot || "",
            shipQty: Number(p.qty),
            shipComplete: true,
          }),
        });
        const lineData = await lineRes.json().catch(() => ({}));
        if (!lineRes.ok || lineData.success === false) {
          // Keep BOTH the pack and the lines that did land, so the retry
          // resumes from here instead of orphaning the pack or re-adding
          // lines Epicor already accepted.
          setPackNum(newPack);
          setCreatedLines(committed);
          throw new Error(lineData.failedReason || lineData.message
            || `Line ${row.orderLine}·${row.orderRelNum} (${row.partNum}) failed to add.`);
        }
        committed.push({
          orderLine: row.orderLine, orderRelNum: row.orderRelNum, partNum: row.partNum,
          lineDesc: row.lineDesc, qty: Number(p.qty), bin: p.bin, lot: p.lot || "",
          packLine: lineData.data?.packLine,
        });
      }

      setPackNum(newPack);
      setCreatedLines(committed);
      setStage(3);
      loadReadiness(newPack);
      if (adopted) {
        setBanner({ type: "info", text: `Reused open pack #${newPack} — it was empty, so no new pack was created.` });
      }
    } catch (e) {
      // Surface the real Epicor reason (createData.failedReason / lineData.failedReason)
      // and scroll it into view so the failure is never silent.
      setBanner({ type: "danger", text: e.message || "Could not create the pack." });
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setCreating(false);
    }
  }

  // ── Stage 3: readiness + recipient patch + address validate + package ───
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);

  const [phonePatched, setPhonePatched] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  // Package editor sits behind the Package tile's "Override →", as in the
  // simulator — the tile already states the weight that will be sent.
  const [pkgOpen, setPkgOpen] = useState(false);
  // Package weight + dimensions are always shown and always sent. They used to
  // be collapsed behind an "Override →" link on the Package tile; that tile is
  // gone, and these are the values that actually reach FedEx.
  const [recip, setRecip] = useState({
    personName: "", companyName: "", phoneNumber: "",
    street: "", city: "", state: "", zip: "", country: "US",
  });
  // Package figures start EMPTY and are seeded from the pack via readiness.
  // They used to default to 5 LB / 10×8×6 and were sent on every shipment,
  // which overrode ShipHead's real values at the carrier — and FedEx bills on
  // weight and dim-weight, so that was a billing error, not a cosmetic one.
  const [pkg, setPkg] = useState({ weight: "", weightUOM: "", length: "", width: "", height: "" });
  const pkgSeeded = useRef(false);

  // ── Package picker ─────────────────────────────────────────────────────────
  // Epicor's own carton catalogue, so a clerk chooses "ENVELOPE-LARGE" instead of
  // typing 12, 9 and 3 from memory. Fetched once per mount and cached 12h server
  // side; 28 rows of master data that change about once a year.
  const [pkgCatalog, setPkgCatalog] = useState({ busy: true, items: [], stale: false });
  const [pkgCode, setPkgCode] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/shipment-entry/pickers/packages`);
        const d = await r.json().catch(() => ({}));
        if (!alive) return;
        setPkgCatalog({
          busy: false,
          items: Array.isArray(d.packages) ? d.packages : [],
          stale: !!d.stale,
        });
      } catch {
        // A dead catalogue must not block shipping. The dimension fields stay
        // open and typeable, which is exactly today's behaviour — the picker is
        // an accelerator, never a gate.
        if (alive) setPkgCatalog({ busy: false, items: [], stale: false });
      }
    })();
    return () => { alive = false; };
  }, []);

  /// Which dimension fields the chosen package has already answered.
  ///
  /// The whole point of locking: if a preset's dimensions cannot be edited, then
  /// ShipHead.PkgCode and the dimensions can never contradict each other. There is
  /// no "edited a preset" state to detect, no flip-to-custom rule, and no
  /// server-side mismatch check — the bad state is unreachable rather than caught.
  /// To use different numbers the clerk picks "? x ? x ?", which states the intent
  /// instead of leaving us to infer it from a keystroke.
  const dimsLocked = useMemo(() => {
    const p = pkgCatalog.items.find((x) => x.code === pkgCode);
    if (!p) return { L: false, W: false, H: false };          // nothing picked — type freely
    if (p.fill === "Sized") return { L: true, W: true, H: true };
    // Pallets know their footprint but not their height: how high it was stacked is
    // a fact about this shipment, which is why Epicor leaves PkgHeight at zero.
    if (p.fill === "Pallet") return { L: true, W: true, H: false };
    return { L: false, W: false, H: false };                   // Custom — all three theirs
  }, [pkgCode, pkgCatalog.items]);

  function applyPackage(code) {
    setPkgCode(code);
    const p = pkgCatalog.items.find((x) => x.code === code);
    if (!p) return;
    setPkg((prev) => ({
      ...prev,
      length: p.fill === "Custom" ? "" : String(p.length || ""),
      width: p.fill === "Custom" ? "" : String(p.width || ""),
      // Pallet height is always the clerk's to give, so clear whatever was there
      // rather than leaving a stale number under a new footprint.
      height: p.fill === "Sized" ? String(p.height || "") : "",
    }));
  }

  const pkgHint = useMemo(() => {
    if (pkgCatalog.busy) return "Loading Epicor's package list…";
    if (!pkgCatalog.items.length)
      return "Package list unavailable — enter the dimensions directly.";
    const p = pkgCatalog.items.find((x) => x.code === pkgCode);
    if (!p) return "Pick a package and its dimensions fill themselves. Weight is always required — FedEx rates on it.";
    if (p.fill === "Sized") return `Dimensions are locked to ${p.code}. To use different ones, pick “? x ? x ?”.`;
    if (p.fill === "Pallet") return `${p.code} is ${fmtQty(p.length)} × ${fmtQty(p.width)}. Enter how high it is stacked.`;
    return "No preset size — enter all three. Rating on weight alone doesn't avoid the dimensional charge, it defers it to an adjustment later.";
  }, [pkgCatalog, pkgCode]);

  /// FedEx bills the greater of actual and dimensional weight. 139 is the standard
  /// US domestic divisor for inch/pound; it is shown as guidance, never sent — the
  /// carrier does its own arithmetic and is the only one whose number counts.
  const dimWeight = useMemo(() => {
    const l = Number(pkg.length) || 0, w = Number(pkg.width) || 0, h = Number(pkg.height) || 0;
    if (!(l > 0 && w > 0 && h > 0)) return 0;
    return Math.ceil((l * w * h) / 139);
  }, [pkg.length, pkg.width, pkg.height]);

  const [addrValidate, setAddrValidate] = useState(null); // { busy, result }

  const loadReadiness = useCallback(async (pack) => {
    const id = pack || packNum;
    if (!id) return;
    setReadinessLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/shipment-entry/${id}/readiness`);
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.success !== false) {
        setReadiness(data);
        // Seed the package editor from the pack's own weight.
        if (data.weight) setPkg((prev) => ({ ...prev, weight: data.weight, weightUOM: data.weightUOM || prev.weightUOM }));
      } else {
        setBanner({ type: "danger", title: "Readiness check failed.", text: data.error || data.message || "" });
      }
    } catch (e) {
      setBanner({ type: "danger", title: "Readiness check failed.", text: e.message || "" });
    } finally {
      setReadinessLoading(false);
    }
  }, [packNum]);

  // Open an existing pack instead of creating a second one. Rehydrates the
  // committed-line list from what Epicor already holds, so Stage 2's
  // "already on the pack" test keeps working and a resumed line is never
  // added twice.
  const resumePack = useCallback(async (pack) => {
    setResuming(true); setBanner(null);
    try {
      const r = await apiFetch(`${API_URL}/api/shipment-entry/${pack}`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        throw new Error(data.error || `Pack ${pack} could not be loaded (HTTP ${r.status}).`);
      }
      if (data.header?.isShipped) {
        throw new Error(`Pack ${pack} has already shipped — it can't be resumed.`);
      }
      const lines = data.lines || [];
      setPackNum(pack);
      setCreatedLines(lines.map((l) => ({
        orderLine: l.orderLine,
        orderRelNum: l.orderRelNum,
        partNum: l.partNum,
        lineDesc: l.lineDesc || "",
        qty: Number(l.shipQty) || 0,
        bin: l.binNum || "",
        lot: l.lotNum || "",
        packLine: l.packLine,
      })));
      // An empty pack has nothing to review — send the clerk to line
      // selection, which is the step it never got past.
      setStage(lines.length ? 3 : 2);
      loadReadiness(pack);
    } catch (e) {
      setBanner({ type: "danger", text: e.message || `Could not open pack ${pack}.` });
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setResuming(false);
    }
  }, [loadReadiness]);

  // Seed the recipient editor from the order's ship-to, so the fields aren't
  // blank when the clerk opens it. This is only a first pass — the order DTO
  // has no structured address, just a preformatted string — so it fills the
  // contact bits and readiness fills the rest below.
  useEffect(() => {
    if (!header.shipToNumName && !header.shipToAddressFormatted) return;
    setRecip((prev) => prev.personName || prev.street ? prev : {
      ...prev,
      // Person and company print as two separate lines on the FedEx label, so
      // don't echo the account name into both — leave person blank when Epicor
      // has no distinct contact, or the label shows the same name twice.
      personName: header.shipToContactName || prev.personName,
      companyName: header.shipToNumName || prev.companyName,
      phoneNumber: header.shipToContactPhoneNum || prev.phoneNumber,
    });
  }, [header.shipToNumName, header.shipToAddressFormatted, header.shipToContactName, header.shipToContactPhoneNum]);

  // Authoritative seed: readiness returns the recipient exactly as the label
  // buy would resolve it (recipientCurrent). Because an override is
  // all-or-nothing, the clerk has to resend the whole party to change one
  // field — so the editor must start from the real resolved values, the way
  // the simulator's form is prefilled. Fields the pack is genuinely missing
  // come back blank, which is exactly what we want the clerk to fill in.
  // Seed the package editor from the pack's own ShipHead figures. Blank stays
  // blank — an unset dimension must read as unset so the clerk supplies it,
  // and so we omit it from the request and let the server use ShipHead.
  useEffect(() => {
    if (!readiness || pkgSeeded.current) return;
    pkgSeeded.current = true;
    const num = (v) => (Number(v) > 0 ? String(Number(v)) : "");
    setPkg((prev) => ({
      weight: num(readiness.weight) || prev.weight,
      weightUOM: readiness.weightUOM || prev.weightUOM || "LB",
      length: num(readiness.pkgLength) || prev.length,
      width: num(readiness.pkgWidth) || prev.width,
      height: num(readiness.pkgHeight) || prev.height,
    }));
    // Nothing on the pack to rate against — open the editor rather than hiding
    // the one required input behind a link.
    if (!(Number(readiness.weight) > 0)) setPkgOpen(true);
  }, [readiness]);

  const recipSeeded = useRef(false);
  // The party the label buy WOULD resolve on its own, captured the moment the editor
  // is seeded from it. This is the baseline an override is measured against — see
  // recipientDiffersFromBaseline below. Held in a ref, not state: it is a fixed point
  // of comparison, and re-rendering on it would defeat the purpose.
  const recipBaseline = useRef(null);
  useEffect(() => {
    const cur = readiness?.recipientCurrent;
    if (!cur || recipSeeded.current) return;
    recipSeeded.current = true;
    setRecip((prev) => {
      const seeded = {
        personName:  cur.personName  || prev.personName  || "",
        companyName: cur.companyName || prev.companyName || "",
        phoneNumber: cur.phoneNumber || prev.phoneNumber || "",
        street: (cur.streetLines || []).filter(Boolean).join(", ") || prev.street || "",
        city:   cur.city || prev.city || "",
        state:  cur.stateOrProvinceCode || prev.state || "",
        zip:    cur.postalCode || prev.zip || "",
        country: cur.countryCode || prev.country || "US",
      };
      recipBaseline.current = seeded;
      return seeded;
    });
  }, [readiness]);

  function patchPhone() {
    // The simulator's "missing phone" scenario. The editor is prefilled from
    // readiness.recipientCurrent, so the clerk typically only has to type the
    // phone — but the whole block is what gets sent, since recipientOverride
    // replaces the party outright rather than merging.
    setPhonePatched(true);
    setAddrOpen(true);
    setBanner(null);
  }

  async function runAddressValidate() {
    if (addrValidate?.busy) return;
    // Epicor's ship-to street is MULTI-LINE; the editor holds it comma-joined in one field
    // (see the seed at recipSeeded and applyResolvedAddress). Sending that as a single
    // streetLine made FedEx read "8300 Sheen Drive, Test Address" as one street, resolve it
    // to the non-address "8300 SHEEN DRIVE TEST ADDRESS", and answer UNKNOWN — which is the
    // yellow "could not fully verify" panel on a perfectly good address. Split it back into
    // the lines it came from; FedEx accepts up to three. Measured on this exact address:
    //     one joined line  → "8300 SHEEN DRIVE TEST ADDRESS", isValid false, UNKNOWN
    //     two split lines  → "8300 SHEEN DR", 33709-2222,     isValid true,  BUSINESS
    const streetLines = recip.street
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 3);
    if (!streetLines.length || !recip.country) return; // needs at least street + country
    setAddrValidate({ busy: true, result: null });
    try {
      const r = await apiFetch(`${API_URL}/api/fedex/address/validate`, {
        method: "POST",
        body: JSON.stringify({
          address: {
            streetLines,
            city: recip.city || null,
            stateOrProvinceCode: recip.state || null,
            postalCode: recip.zip || null,
            countryCode: recip.country,
          },
          // MUST be true. FedEx returns its standardized street lines and the ZIP+4 only
          // in the resolution-token fields, and FedExAddressValidateHandler reads them from
          // exactly there (StreetLines = resolved.streetLinesToken, PostalCode =
          // TryExtractPostalCode). With this off, the same real address came back
          // isValid:false / UNKNOWN because the fields the verdict tests were simply absent
          // — a bool? that FedEx never set fails `== true`. Measured against the pilot
          // account on this very address:
          //     off → streetLines ["8300 Sheen Drive"], zip "33709",      isValid false, UNKNOWN
          //     on  → streetLines ["8300 SHEEN DR"],    zip "33709-2222", isValid true,  BUSINESS
          // So this one flag is the difference between warning a clerk about a perfectly
          // good address and confirming it, with the carrier's own ZIP+4 to adopt.
          includeResolutionTokens: true,
          clientReferenceId: packNum ? `pack-${packNum}-recipient` : null,
        }),
      });
      const data = await r.json().catch(() => ({}));
      const v = (r.ok && data.success !== false) ? (data.data || null) : null;
      setAddrValidate({ busy: false, result: v, error: v ? null : (data.failedReason || "Validation failed") });
    } catch (e) {
      setAddrValidate({ busy: false, result: null, error: e.message });
    }
  }

  // Validate the address that will ACTUALLY ship — not just one the clerk
  // happened to retype.
  //
  // This only ever ran on blur of the override fields, which live inside an
  // editor that starts closed. A clerk who didn't need to change anything —
  // the normal case — bought a label against an address nothing had checked.
  // `recip` is seeded from readiness.recipientCurrent, which is the recipient
  // exactly as the label buy resolves it, so validating it here covers the
  // default path. Fires once per pack; edits still re-validate on blur.
  const addrAutoChecked = useRef(false);
  useEffect(() => { addrAutoChecked.current = false; }, [packNum]);
  useEffect(() => {
    if (addrAutoChecked.current) return;
    if (!recip.street || !recip.country) return;
    addrAutoChecked.current = true;
    runAddressValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recip.street, recip.country]);

  // Adopt FedEx's corrected address.
  //
  // Note this necessarily turns the recipient into an OVERRIDE, because that's
  // the only way to send a different address than the pack's ShipTo. Overrides
  // are all-or-nothing (see below), so the editor is opened at the same time —
  // if the phone is blank, the clerk needs to see the field that's now
  // required rather than discover it via a disabled button.
  function applyResolvedAddress() {
    const ra = addrValidate?.result?.resolvedAddress;
    if (!ra) return;
    setRecip((p) => ({
      ...p,
      street: (ra.streetLines || []).filter(Boolean).join(", ") || p.street,
      city: ra.city || p.city,
      state: ra.stateOrProvinceCode || p.state,
      zip: ra.postalCode || p.zip,
      country: ra.countryCode || p.country,
    }));
    setAddrOpen(true);
  }

  // Does the clerk have recipient edits worth sending as an override?
  //
  // This must be all-or-nothing. FedExPartyResolver.MapOverride uses the
  // override verbatim — it does NOT merge missing fields back from the ShipTo
  // master — so a half-filled override is strictly worse than none: it wipes
  // the good address and the job hard-fails 422 at RateShop. Sending nothing
  // is safe, because the resolver then falls back to the pack's ShipTo.
  //
  // So "the clerk touched the editor" is not the test; "the clerk produced a
  // complete, shippable party" is.
  const recipientOverrideComplete = !!(
    recip.street.trim() && recip.city.trim() && recip.state.trim()
    && recip.zip.trim() && recip.country.trim() && recip.phoneNumber.trim()
  );

  // Is there anything to override?
  //
  // Measured by COMPARING the editor against the baseline the server would resolve on
  // its own — never by asking whether a panel happens to be open.
  //
  // The previous test was `phonePatched || addrOpen`. Because applyResolvedAddress sets
  // addrOpen, adopting FedEx's corrected address armed the override; but closing the
  // editor with the × cleared addrOpen, so buildRecipientOverride returned null and the
  // label was bought against the ORIGINAL ship-to — silently, because the only guard
  // (recipientOverridePartial) fires on INCOMPLETE overrides, and this one was complete.
  // A panel's visibility must never decide what gets printed on a parcel.
  const recipientDiffersFromBaseline = useMemo(() => {
    const base = recipBaseline.current;
    if (!base) return phonePatched;   // not seeded yet — fall back to the explicit action
    // Case- and whitespace-insensitive: re-typing "st petersburg" as "St. Petersburg" is
    // not an override, and sending one costs an all-or-nothing party round trip for nothing.
    const norm = (v) => (v ?? "").trim().replace(/\s+/g, " ").toUpperCase();
    return ["personName", "companyName", "phoneNumber", "street", "city", "state", "zip", "country"]
      .some((k) => norm(recip[k]) !== norm(base[k]));
  }, [recip, phonePatched]);

  // Partial edits are the dangerous state — flag them rather than shipping them.
  const recipientOverridePartial = recipientDiffersFromBaseline && !recipientOverrideComplete;
  const hasRecipientOverride = recipientDiffersFromBaseline && recipientOverrideComplete;
  function buildRecipientOverride() {
    if (!hasRecipientOverride) return null;
    return {
      personName: recip.personName || null,
      companyName: recip.companyName || null,
      phoneNumber: recip.phoneNumber || null,
      address: {
        streetLines: [recip.street].filter(Boolean),
        city: recip.city || null,
        stateOrProvinceCode: recip.state || null,
        postalCode: recip.zip || null,
        countryCode: recip.country || "US",
      },
    };
  }

  const recipientReady = readiness ? (readiness.recipientReady || hasRecipientOverride) : false;
  // Readiness can't "see" a recipientOverride (it inspects the pack, not the
  // pending override), so a patched phone/address keeps canShip=false on
  // re-check. Allow continue when the ONLY unready section is the recipient
  // and the clerk has supplied an override — the server validates for real at
  // generate time. Shipper/package blockers still hard-stop.
  // A weight the clerk has typed satisfies the package requirement even though
  // readiness (which inspects the persisted pack) still reports it missing —
  // the value rides along on generate-and-ship. Without this, a weightless pack
  // could never be shipped from this screen.
  const packageSatisfied = !!readiness && (readiness.packageReady || Number(pkg.weight) > 0);
  // Stated as the three sections plus the shipped-guard, rather than as
  // "canShip OR a special case". readiness.canShip reflects the PERSISTED pack,
  // so it stays false for anything the clerk has fixed in the form but not yet
  // sent (weight typed here, recipient override pending) — leaning on it alone
  // left those legitimately-fixable states unshippable.
  const canContinueToService = !readiness ? false : (
    !recipientOverridePartial
    && !readiness.isShipped
    && readiness.shipperReady
    && packageSatisfied
    && (readiness.recipientReady || hasRecipientOverride)
  );

  // ── Stage 4: service + billing + reference ───────────────────────────────
  const [serviceType, setServiceType] = useState("");
  const [paymentType, setPaymentType] = useState("SENDER");
  const [payorAccount, setPayorAccount] = useState("");
  const [customerRef, setCustomerRef] = useState("");
  const [forceReship, setForceReship] = useState(false);

  // Default the service/payment/ref off readiness when it arrives.
  //
  // currentShipViaService is a reverse lookup through ShipViaCodeMap, so it is
  // null whenever that map is unconfigured — which used to leave Stage 4 with
  // no service selected and no visible way to pick one. Fall back to Priority
  // Overnight (the simulator's default), then to whatever the catalog leads
  // with, so a service is always selected.
  useEffect(() => {
    if (!readiness) return;
    const catalog = readiness.availableServices || [];
    const has = (t) => catalog.some((s) => s.serviceType === t);
    const fallback =
      (readiness.currentShipViaService && has(readiness.currentShipViaService) ? readiness.currentShipViaService : null)
      || (has("PRIORITY_OVERNIGHT") ? "PRIORITY_OVERNIGHT" : null)
      || catalog[0]?.serviceType
      || "";
    setServiceType((prev) => prev || fallback);
    setPaymentType((prev) => prev !== "SENDER" ? prev : (readiness.currentPaymentType || "SENDER"));
    setCustomerRef((prev) => prev || `Order ${orderNum}`);
  }, [readiness, orderNum]);

  // Memoised so the `|| []` fallback doesn't hand orderedServices a fresh array
  // identity on every render.
  const services = useMemo(() => readiness?.availableServices || [], [readiness]);
  const alreadyLabeled = !!readiness?.existingTrackingNumber;
  const payorMissing = NEEDS_PAYOR_ACCOUNT(paymentType) && !payorAccount.trim();

  // ── Service list ordering ────────────────────────────────────────────────
  // Every catalog entry is selectable. `supported` on the DTO means only "we
  // have an Epicor Ship Via code to stamp after the label is bought" — the
  // backend's own contract says rate-shop and buy-label both work without it.
  // Filtering the list on it (as this page used to) meant that an unconfigured
  // ShipViaCodeMap — which is the shipped state of appsettings.Production.json —
  // rendered zero radio buttons and dead-ended the clerk at Stage 4.
  // Catalog order comes from the server (FedExServiceCatalog.All documents
  // "Order matters — this is the order the picker renders"), so it is used
  // as-is rather than re-sorted here. The only local decision is how many to
  // show before the disclosure; services the pack can actually stamp lead,
  // which is data-driven off `supported`, not a baked-in list of service codes.
  const orderedServices = useMemo(() => {
    const supported = services.filter((s) => s.supported);
    const rest = services.filter((s) => !s.supported);
    return [...supported, ...rest];
  }, [services]);
  const visibleCount = Math.max(
    orderedServices.filter((s) => s.supported).length,
    Math.min(CORE_SERVICE_COUNT, orderedServices.length),
  );
  const coreServices = orderedServices.slice(0, visibleCount);
  const moreServices = orderedServices.slice(visibleCount);
  const selectedService = services.find((s) => s.serviceType === serviceType) || null;

  function renderServiceCard(s) {
    const sel = serviceType === s.serviceType;
    const blurb = SERVICE_BLURBS[s.serviceType];
    return (
      <label key={s.serviceType} style={{ display: "flex", gap: 12, alignItems: "center", padding: 12, borderRadius: 12, cursor: "pointer", border: `2px solid ${sel ? SEL_BORDER : IDLE_BORDER}`, background: sel ? SEL_BG : "#fff" }}>
        <input type="radio" name="svc" checked={sel} style={{ accentColor: PURPLE }}
          onChange={() => setServiceType(s.serviceType)} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            {s.serviceName}
            {readiness?.currentShipViaService === s.serviceType && <span className="qw-pill qw-pill-violet">DEFAULT</span>}
          </div>
          <div style={{ fontSize: 12, color: "#94A3B8" }}>
            {blurb ? `${blurb} · ` : ""}
            {s.epicorShipViaCode || s.serviceType}
          </div>
        </div>
      </label>
    );
  }

  // ── Stage 5: generate + progress + result + pack-slip poll ──────────────
  const [, setGenerating] = useState(false);         // in-flight flag (setter only)
  const [genStep, setGenStep] = useState(0);        // 0..6 progress cursor
  const [result, setResult] = useState(null);       // CreateFullShipmentResponse
  const [genError, setGenError] = useState(null);
  // idle | polling | ready | timedout (submitted, still rendering) | rejected (never accepted)
  const [slip, setSlip] = useState({ status: "idle", url: null });
  // The simulator's "auto-open the label as soon as it's ready". OFF by
  // default: this fires without a user gesture, so browsers treat it as a popup
  // and block it — which produced a scary "your browser blocked the label"
  // banner on every successful shipment and made a working label look broken.
  // Opt in and allow pop-ups for the site if you want it.
  const [autoOpenLabel, setAutoOpenLabel] = useState(false);
  const labelOpened = useRef(false);
  // Wraps the hidden <ShipmentLabelCard>; its innerHTML is what openLabelPreview
  // writes into the popup.
  const labelPreviewRef = useRef(null);
  const genTimer = useRef(null);
  const slipTimer = useRef(null);
  // The blob URL currently backing the Pack Slip card. Held in a ref as well as in
  // state so the unmount cleanup can revoke it — an un-revoked blob pins the whole
  // PDF in memory until the page reloads, and the Done screen is left open a lot.
  const slipUrlRef = useRef(null);

  const GEN_STEPS = [
    { title: "PreFlight",    sub: "Validate pack state & idempotency" },
    { title: "RateShop",     sub: "FedEx /rate/v1/rates/quotes" },
    { title: "BuyLabel",     sub: "FedEx /ship/v1/shipments & Epicor stamp" },
    { title: "MarkShipped",  sub: "Epicor Stage-3 UpdateMaster" },
    { title: "PackSlip",     sub: "Make & save the pack slip PDF" },
    { title: "Verify",       sub: "GetByID final state check" },
  ];

  function buildGeneratePayload() {
    const payload = { skipPackSlip: false };
    if (serviceType) payload.serviceType = serviceType;
    // Always send the billing variant. The simulator omits it when it equals
    // SENDER, but the server treats "absent" as "reverse-look-up the pack's
    // ShipViaCode" (CreateFullShipmentHandler: effectivePaymentType), so a pack
    // whose ShipVia maps to THIRD_PARTY would silently bill the customer even
    // though the clerk explicitly chose SENDER. An explicit choice must win.
    if (paymentType) payload.paymentType = paymentType;
    if (payorAccount.trim()) payload.payorAccountNumber = payorAccount.trim();
    if (customerRef.trim()) payload.customerReference = customerRef.trim();
    // Send a package figure only when we actually have one. Omitted fields
    // fall back to ShipHead.Weight / PkgLength / PkgWidth / PkgHeight server
    // side (documented on FedExRatesRequest), so blanks defer to Epicor
    // instead of overwriting it with an invented number.
    const pos = (v) => (Number(v) > 0 ? Number(v) : null);
    if (pos(pkg.weight) !== null) payload.weight = pos(pkg.weight);
    if (pkg.weightUOM) payload.weightUOM = pkg.weightUOM;
    if (pos(pkg.length) !== null) payload.length = Math.round(pos(pkg.length));
    if (pos(pkg.width) !== null) payload.width = Math.round(pos(pkg.width));
    if (pos(pkg.height) !== null) payload.height = Math.round(pos(pkg.height));
    if (pos(pkg.length) !== null || pos(pkg.width) !== null || pos(pkg.height) !== null) {
      payload.dimensionsUOM = readiness?.pkgSizeUOM || "IN";
    }
    const ro = buildRecipientOverride();
    if (ro) payload.recipientOverride = ro;
    return payload;
  }

  // Advance the progress steps on a timer while the single orchestrator call
  // is in flight. We can't observe sub-steps (it's one request), so we creep
  // forward and hold on the last step until the real response resolves.
  function startProgressAnimation() {
    setGenStep(0);
    const holdAt = GEN_STEPS.length - 1;
    genTimer.current = setInterval(() => {
      setGenStep((s) => (s < holdAt ? s + 1 : s));
    }, 1400);
  }
  function stopProgressAnimation() {
    if (genTimer.current) { clearInterval(genTimer.current); genTimer.current = null; }
  }

  async function runGenerate() {
    setStage(5);
    setGenerating(true); setGenError(null); setResult(null);
    setSlip({ status: "idle", url: null });
    startProgressAnimation();
    try {
      const url = `${API_URL}/api/shipment-entry/${packNum}/generate-and-ship${forceReship ? "?force=true" : ""}`;
      const r = await apiFetch(url, { method: "POST", body: JSON.stringify(buildGeneratePayload()) });
      const data = await r.json().catch(() => ({}));
      stopProgressAnimation();
      if (!r.ok || data.success === false || !data.data) {
        setGenStep(0);
        throw new Error(data.failedReason || data.message || `Shipment failed (HTTP ${r.status}).`);
      }
      setGenStep(GEN_STEPS.length);
      setResult(data.data);
      // Open the pack slip the ship step already made and saved (packSlipDocumentId,
      // 2026-10-25) instead of building a second copy; when step 5 could not make one,
      // loadPackSlip builds it fresh. skipPackSlip is still honoured: it means the caller
      // asked for no packing slip at all, which is a choice, not a failure.
      if (!data.data.skipPackSlip) loadPackSlip(data.data.packSlipDocumentId);
      else setSlip({ status: "idle", url: null });
      // Dispatcher work queue (SQL/048): a successful generate-and-ship IS the
      // completion event for this order's assignment. Best-effort — most
      // orders carry no assignment and the endpoint answers 200 either way.
      apiFetch(`${API_URL}/api/dispatch/complete-by-order/${encodeURIComponent(orderNum)}?packNum=${encodeURIComponent(packNum)}`,
        { method: "POST" }).catch(() => {});
    } catch (e) {
      setGenError(e.message || "Shipment failed.");
    } finally {
      setGenerating(false);
    }
  }

  // Fetch OUR OWN pack slip — GET /api/reports/packslip/{packNum}?format=pdf.
  //
  // WHY THIS REPLACED THE EPICOR SSRS PATH.
  // The card used to POST /api/shipments/{packNum}/pack-slip, get a taskGuid, then poll
  // /api/shipments/report/{taskGuid} for up to ~40s while Epicor's report agent rendered
  // an SSRS PackingSlip. That path has three failure modes this one simply does not have:
  //   • It depends on Ice.BO.ReportStyleSvc to discover a valid per-tenant report style.
  //     That service returns 404 on this tenant, so the style guard never resolves, the
  //     submit falls back to a guessed style number, and Epicor refuses it — which is the
  //     "Couldn't be printed" state the card kept landing in.
  //   • Submit-then-poll is asynchronous, so a slow render looked like a failure.
  //   • The layout lived in an Epicor report style we cannot edit from this repo.
  // Ours renders synchronously through the same pipeline as the quote, order ack and AR
  // invoice, so the four documents look like one family and we control the layout.
  //
  // The response is fetched with apiFetch (which carries the bearer token) and turned into
  // a blob URL rather than linked directly: the endpoint needs an Authorization header, and
  // putting a token in a URL is exactly the impersonation hole we do not want to widen.
  // The SSRS endpoints are left in place on the server — this is a client cut-over only.
  async function loadPackSlip(savedDocumentId) {
    if (!packNum) return;
    if (slipTimer.current) { clearTimeout(slipTimer.current); slipTimer.current = null; }
    if (slipUrlRef.current) { URL.revokeObjectURL(slipUrlRef.current); slipUrlRef.current = null; }
    setSlip({ status: "polling", url: null });
    try {
      // The saved copy is the exact PDF the ship step archived — reading it stores nothing new.
      const r = await apiFetch(savedDocumentId
        ? `${API_URL}/api/reports/document/${encodeURIComponent(savedDocumentId)}`
        : `${API_URL}/api/reports/packslip/${encodeURIComponent(packNum)}?format=pdf`);
      if (!r.ok) {
        // The body is JSON on failure and a PDF on success, so only read it here.
        const data = await r.json().catch(() => ({}));
        setSlip({ status: "rejected", url: null });
        setBanner({ type: "warning", text: data.message || data.error || "The packing slip could not be generated." });
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      slipUrlRef.current = url;
      setSlip({ status: "ready", url });
    } catch (e) {
      setSlip({ status: "rejected", url: null });
      setBanner({ type: "warning", text: e.message || "The packing slip could not be generated." });
    }
  }

  // The card's retry is "try generating it again" — always a fresh build.
  async function retryPackSlip() { await loadPackSlip(); }

  // Clean up timers on unmount.
  useEffect(() => () => {
    stopProgressAnimation();
    if (slipTimer.current) clearTimeout(slipTimer.current);
    if (slipUrlRef.current) { URL.revokeObjectURL(slipUrlRef.current); slipUrlRef.current = null; }
  }, []);

  // window.open returns null when the browser blocks the popup. Silently doing
  // nothing in that case is indistinguishable from "the button is broken", so
  // say what happened and leave the card as the manual route.
  function openInNewTab(url, what) {
    const w = window.open(url, "_blank", "noopener");
    if (!w) {
      setBanner({
        type: "warning",
        text: `Your browser blocked the ${what} from opening in a new tab. Allow pop-ups for this site, or click the ${what} card to open it.`,
      });
      return false;
    }
    return true;
  }
  // Open the rendered label (barcode + addresses + service band) in its own
  // window, the way the simulator does — instead of parking it inline on the
  // Done screen where it pushed the action buttons below the fold.
  //
  // The markup is CLONED from the hidden <ShipmentLabelCard> below rather than
  // rebuilt as a template string: that keeps one source of truth and, more to
  // the point, reuses the real Code 128 SVG. A hand-written copy would drift
  // from the encoder and produce bars that scan to the wrong tracking number.
  // The popup gets its own copy of the two utility classes the card leans on
  // (.qw-eyebrow / .qw-mono), since the app's stylesheet isn't loaded there.
  function openLabelPreview() {
    const node = labelPreviewRef.current;
    if (!node) return;
    // A direct click IS a user gesture, so window.open is not popup-blocked
    // here — unlike the auto-open effect below, which fires without one.
    const w = window.open("", "_blank", "width=560,height=860");
    if (!w) {
      setBanner({
        type: "warning",
        text: "Your browser blocked the label preview window. Allow pop-ups for this site and click the card again.",
      });
      return;
    }
    const pdfLink = result?.labelDownloadUrl
      ? `<a class="pdf" href="${result.labelDownloadUrl}" target="_blank" rel="noopener noreferrer">Open the official FedEx label PDF &#8599;</a>`
      : `<p class="note">The official FedEx PDF isn't available for this shipment. This preview is a confirmation aid — it is not the label that ships.</p>`;
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8">` +
      `<title>FedEx Label — Order ${orderNum || ""}</title>` +
      `<style>` +
      `body{margin:0;padding:20px;background:#F1F5F9;` +
      `font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#0F172A}` +
      `.qw-eyebrow{font-size:11px;font-weight:700;color:#64748B;text-transform:uppercase;letter-spacing:.06em}` +
      `.qw-mono{font-family:'JetBrains Mono',ui-monospace,monospace}` +
      `.pdf{display:block;max-width:520px;margin:16px auto 0;padding:11px 16px;border-radius:10px;` +
      `background:#4C1D95;color:#fff;font-weight:600;font-size:13.5px;text-align:center;text-decoration:none}` +
      `.note{max-width:520px;margin:16px auto 0;font-size:12px;color:#92400E;background:#FFFBEB;` +
      `border:1px solid #FDE68A;border-radius:10px;padding:10px 12px}` +
      `@media print{body{background:#fff;padding:0}.pdf,.note{display:none}}` +
      `</style></head><body>${node.innerHTML}${pdfLink}</body></html>`
    );
    w.document.close();
  }

  // Auto-open the label once it lands, as the simulator does. Guarded by a ref
  // so a re-render can't reopen it, and it degrades to the banner above if the
  // browser blocks the popup (likely here — this fires without a click).
  useEffect(() => {
    if (!autoOpenLabel || labelOpened.current) return;
    if (!result?.labelDownloadUrl) return;
    labelOpened.current = true;
    openInNewTab(result.labelDownloadUrl, "label");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, autoOpenLabel]);

  function resetToStart() {
    stopProgressAnimation();
    if (slipTimer.current) clearTimeout(slipTimer.current);
    navigate(`/order/${encodeURIComponent(orderNum)}`);
  }

  // ── Guards on navigation ────────────────────────────────────────────────
  function goStage(n) { setBanner(null); setStage(n); }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="qw-root content-halo" style={{
      padding: "28px 24px 48px", maxWidth: "min(1080px, 96vw)", margin: "84px auto 56px",
      borderRadius: 16, minHeight: "calc(100vh - 140px)",
      // Scoped to this page so the Quote wizard keeps its indigo theme.
      "--qw-primary": PURPLE, "--qw-primary-dark": PURPLE_DARK,
    }}>
      {/* Header */}
      {/* Brand gradient from the simulator: purple → violet → FedEx orange. The
          shared .qw-header ships the Quote wizard's indigo ramp, so override here. */}
      <div className="qw-header" style={{
        marginBottom: 16,
        background: `linear-gradient(135deg, ${PURPLE} 0%, #6B21A8 50%, ${ORANGE} 100%)`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="qw-header-icon"><FaTruck size={18} /></div>
          <div>
            <div className="qw-header-title">Generate &amp; Ship — Order #{orderNum}</div>
            <div className="qw-header-sub">{header.customerName || header.customerCustID || "FedEx shipment"}{packNum ? ` · Pack #${packNum}` : ""}</div>
          </div>
        </div>
        <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }}
          onClick={() => navigate(`/order/${encodeURIComponent(orderNum)}`)}>
          <FaChevronLeft style={{ marginRight: 6 }} /> Order
        </Button>
      </div>

      <StagePills stage={stage} />

      {banner && (
        <Alert variant={banner.type} dismissible onClose={() => setBanner(null)} style={{ fontSize: 13 }}>
          {/* A titled banner always leads with its own words; the server's reason follows only
              when it survives as a sentence, so the generic "Something went wrong…" never
              replaces the title (UI-SHP-048). */}
          {banner.title && <div style={{ fontWeight: 600 }}>{banner.title}</div>}
          {banner.text && (!banner.title || (
              !/^Something went wrong/.test(toDisplayMessage(banner.text))
              // Exception / stack text is for the console, not under a clear title.
              && !/Exception|Object reference|\bat\s+\w+(?:\.\w+){2,}/.test(banner.text)))
            && <ErrorBody raw={banner.text} />}
        </Alert>
      )}

      {/* ============ STAGE 1: ORDER ============ */}
      {stage === 1 && (
        <div className="qw-card-flat qw-pad">
          {orderLoading ? (
            <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading order…</div>
          ) : orderErr ? (
            <Alert variant="danger" style={{ fontSize: 13 }}><ErrorBody raw={orderErr} /></Alert>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div className="qw-eyebrow">Order detail</div>
                  <div className="qw-h2">Order #{orderNum}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span className="qw-pill qw-pill-emerald">{header.openOrder === false ? "CLOSED" : "OPEN"}</span>
                  {header.orderHeld && <span className="qw-pill" style={{ background: "#FEF3C7", color: "#92400E" }}>HELD</span>}
                  {header.custOnCreditHold && <span className="qw-pill" style={{ background: "#FEE2E2", color: "#991B1B" }}>CREDIT HOLD</span>}
                </div>
              </div>

              <div className="qw-grid-3" style={{ marginTop: 14 }}>
                <Meta label="Customer" value={header.customerCustID} sub={header.customerName} />
                <Meta label="Plant" value={header.plant} sub={header.plantName} />
                <Meta label="Ship Via" value={header.shipViaCode} sub={header.shipViaCodeDescription} />
              </div>

              <div style={{ marginTop: 16, borderRadius: 12, background: "#FAFAFB", padding: 14 }}>
                <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Ship to</div>
                <div style={{ fontWeight: 600 }}>{header.shipToNumName || "—"}</div>
                <div style={{ fontSize: 13, color: "#475569", whiteSpace: "pre-wrap" }}>{header.shipToAddressFormatted || ""}</div>
              </div>

              {shipBlockReason && (
                <Alert variant="danger" style={{ fontSize: 13, marginTop: 16, marginBottom: 0 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, marginBottom: 2 }}>This order can’t be shipped yet</div>
                      {shipBlockReason}
                    </div>
                  </div>
                </Alert>
              )}

              {/* Open packs already on this order — reuse rather than duplicate. */}
              {openPacks.length > 0 && (
                <Alert variant="warning" style={{ fontSize: 13, marginTop: 16, marginBottom: 0 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    This order already has {openPacks.length} open pack{openPacks.length === 1 ? "" : "s"}
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    {reusablePack
                      ? <>Pack <strong>#{reusablePack.packNum}</strong> is open and empty, so continuing will
                         add your lines to it rather than creating another one.</>
                      : <>Resume one to keep adding to it. Creating another is fine for a genuine
                         split shipment, but starting fresh by mistake leaves an empty pack behind.</>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {openPacks.map((p) => {
                      const n = packLines ? packLines[p.packNum] : undefined;
                      const willReuse = reusablePack?.packNum === p.packNum;
                      return (
                        <div key={p.packNum} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#fff", border: `1px solid ${willReuse ? "#A78BFA" : "#FDE68A"}`, borderRadius: 10, padding: "8px 12px" }}>
                          <div>
                            <span className="qw-mono" style={{ fontWeight: 700 }}>Pack #{p.packNum}</span>{" "}
                            <span style={{ color: "#64748B" }}>
                              {p.shipStatus || "OPEN"}
                              {p.shipDate ? ` · ${new Date(p.shipDate).toLocaleDateString("en-US")}` : ""}
                              {p.trackingNumber ? ` · ${p.trackingNumber}` : ""}
                            </span>{" "}
                            {packLines === null ? (
                              <span style={{ color: "#94A3B8" }}>· checking…</span>
                            ) : n === 0 ? (
                              <span className="qw-pill qw-pill-indigo">EMPTY</span>
                            ) : n > 0 ? (
                              <span style={{ color: "#64748B" }}>· {n} line{n === 1 ? "" : "s"}</span>
                            ) : (
                              // null → the per-pack lookup failed. Don't imply it's empty.
                              <span style={{ color: "#94A3B8" }}>· contents unknown</span>
                            )}
                          </div>
                          {willReuse ? (
                            <span style={{ color: "#4D148C", fontWeight: 700 }}>Will be reused ↓</span>
                          ) : (
                            <Button size="sm" className="qw-btn qw-btn-primary"
                              style={{ background: PURPLE, borderColor: "transparent" }}
                              disabled={resuming} onClick={() => resumePack(p.packNum)}>
                              {resuming ? <><Spinner animation="border" size="sm" /> Opening…</> : "Resume this pack →"}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Alert>
              )}

              {/* The lookup failed. Saying nothing here would read as "no open
                  packs", which is the assumption that creates duplicates. */}
              {shipmentsUnknown && (
                <Alert variant="secondary" style={{ fontSize: 13, marginTop: 16, marginBottom: 0 }}>
                  Couldn't check whether this order already has open packs — that's not the
                  same as it having none. Check the order's Shipments tab before creating one,
                  or you may end up with a duplicate.
                </Alert>
              )}

              <div style={{ marginTop: 16 }}>
                <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Shippable lines ({shippableRows.length})</div>
                {shippableRows.length === 0 ? (
                  <Alert variant="warning" style={{ fontSize: 13 }}>
                    No open, unshipped lines found on this order. Nothing to ship.
                  </Alert>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {shippableRows.map((row) => (
                      <div key={row.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #EEF0F3", borderRadius: 10, padding: "10px 12px" }}>
                        <div>
                          <span className="qw-mono" style={{ color: "#94A3B8", fontSize: 12 }}>L{row.orderLine}·{row.orderRelNum}</span>{" "}
                          <span style={{ fontWeight: 600 }}>{row.partNum}</span>{" "}
                          <span style={{ color: "#64748B", fontSize: 13 }}>{row.lineDesc}</span>
                        </div>
                        <div style={{ color: "#64748B", fontSize: 12, whiteSpace: "nowrap" }}>
                          Open {fmtQty(row.remaining)} {row.uom}{row.warehouseCode ? ` · ${row.warehouseCode}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="qw-actions" style={{ marginTop: 20 }}>
                {/* Reusing an empty pack is the normal path when one exists, so
                    it keeps the primary styling; only "make a second pack
                    alongside an in-progress one" is demoted to a quiet choice. */}
                <Button className={openPacks.length > 0 && !reusablePack ? "qw-btn qw-btn-ghost" : "qw-btn qw-btn-primary"}
                  style={openPacks.length > 0 && !reusablePack
                    ? { width: "100%" }
                    : { background: PURPLE, borderColor: "transparent", width: "100%" }}
                  disabled={shippableRows.length === 0 || !!shipBlockReason || resuming}
                  onClick={() => goStage(2)}>
                  <FaTruck style={{ marginRight: 6 }} />
                  {reusablePack
                    ? `Continue — reuses pack #${reusablePack.packNum} →`
                    : openPacks.length > 0 ? "Create a new pack instead →" : "Ship this order →"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ============ STAGE 2: LINES ============ */}
      {stage === 2 && (
        <div className="qw-card-flat qw-pad">
          <div className="qw-eyebrow">Select lines &amp; pick inventory</div>
          <div className="qw-h2" style={{ marginBottom: 4 }}>Which lines to ship?</div>
          <p style={{ color: "#64748B", fontSize: 13, marginBottom: 12 }}>
            Tick each line, set the qty, then pick a bin and lot. Bins + lots are queried live per part from inventory pickers.
          </p>

          {shippableRows.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
              <Button size="sm" className="qw-btn qw-btn-outline" disabled={autoBusy || creating} onClick={autoSelectAll}>
                {autoBusy
                  ? (<><Spinner animation="border" size="sm" /> Auto-selecting {autoProgress}…</>)
                  : "⚡ Auto select bins & lots"}
              </Button>
              <span style={{ fontSize: 12, color: "#94A3B8" }}>
                Fills every blank line with the earliest-expiring lot that covers its qty. Rental bins are
                skipped; lines you already picked are left alone.
              </span>
            </div>
          )}

          {packNum && pendingLineCount === 0 && (
            <Alert variant="info" style={{ fontSize: 13 }}>
              Pack <strong>#{packNum}</strong> is created and every selected line is on it — continue to review.
            </Alert>
          )}

          {/* Pack-level defaults are not shown here.
              • Division and the creation Ship Via come from SHIP_DIVISION /
                SHIP_VIA_CREATE_CODE (env-configurable) — neither has an API
                source and neither is a per-shipment decision.
              • Weight is NOT sent at creation. It is a physical measurement,
                so the pack is created weightless and Stage 3 collects the real
                value against readiness. */}

          {shippableRows.map((row) => {
            const p = picks[row.key] || {};
            // Lock a line only once Epicor has actually accepted it. Locking on
            // "a pack exists" meant that after a rejected line-add the clerk
            // could not change the bin that caused the rejection.
            const locked = createdLines.some(
              (c) => c.orderLine === row.orderLine && c.orderRelNum === row.orderRelNum);
            return (
              <div key={row.key} style={{ borderRadius: 12, border: `2px solid ${p.checked ? SEL_BORDER : IDLE_BORDER}`, background: p.checked ? SEL_BG : "#fff", padding: 14, marginBottom: 12, opacity: p.checked ? 1 : 0.6 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  {/* accentColor overrides Bootstrap's default blue tick. */}
                  <Form.Check type="checkbox" checked={!!p.checked} disabled={locked}
                    onChange={(e) => patchPick(row.key, { checked: e.target.checked })}
                    style={{ marginTop: 2, accentColor: PURPLE }} />
                  <div style={{ flex: 1 }}>
                    <div>
                      <span className="qw-mono" style={{ color: "#94A3B8", fontSize: 12 }}>L{row.orderLine}·{row.orderRelNum}</span>{" "}
                      <span style={{ fontWeight: 600 }}>{row.partNum}</span>
                    </div>
                    <div style={{ fontSize: 13, color: "#64748B" }}>{row.lineDesc}</div>
                    <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
                      Open {fmtQty(row.remaining)} {row.uom}{row.warehouseCode ? ` · Warehouse ${row.warehouseCode}` : ""}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="qw-eyebrow">Ship qty</div>
                    <Form.Control size="sm" type="number" min="0" max={row.remaining} step="1" disabled={!p.checked || locked}
                      value={p.qty ?? ""} onChange={(e) => patchPick(row.key, { qty: e.target.value })}
                      className="qw-input qw-mono" style={{ width: 76, textAlign: "right" }} />
                  </div>
                </div>

                {p.checked && (
                  <div className="qw-grid-2" style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #EDE9FE" }}>
                    <div>
                      <label className="qw-label">Bin</label>
                      <Form.Select size="sm" className="qw-input qw-mono" value={p.bin || ""} disabled={locked || p.loadingBins}
                        onChange={(e) => { const bin = e.target.value; patchPick(row.key, { bin, autoNote: null }); loadLots(row, bin, p.lotsByBin); }}>
                        <option value="">{p.loadingBins ? "loading bins…" : "— pick a bin —"}</option>
                        {(p.bins || []).map((b) => (
                          <option key={b.binNum} value={b.binNum}>{b.binNum} · {fmtQty(b.onHandQty)} on-hand</option>
                        ))}
                      </Form.Select>
                      <div style={{ fontSize: 11, color: p.binsError ? "#B45309" : "#94A3B8", marginTop: 3 }}>
                        {p.binsError ? `⚠ ${p.binsError}`
                          : p.bins == null ? "Loading bins…"
                          : `✓ ${p.bins.length} bins · ${fmtQty(p.bins.reduce((a, b) => a + Number(b.onHandQty || 0), 0))} on-hand`}
                      </div>
                    </div>
                    <div>
                      <label className="qw-label">Lot</label>
                      <Form.Select size="sm" className="qw-input qw-mono" value={p.lot || ""} disabled={locked || !p.bin || p.loadingLots}
                        onChange={(e) => patchPick(row.key, { lot: e.target.value })}>
                        <option value="">
                          {!p.bin ? "— pick a bin first —" : p.loadingLots ? "loading lots…" : (p.lots && p.lots.length === 0) ? "no lots (non-lot part)" : "— pick a lot —"}
                        </option>
                        {(p.lots || []).map((l) => (
                          <option key={l.lotNum} value={l.lotNum}>{l.lotNum} · {fmtQty(l.onHandQty)} on-hand</option>
                        ))}
                      </Form.Select>
                      {/* The lot dropdown's "on-hand" is that lot's total across
                          ALL bins, which is not what gets decremented here. Once
                          a lot is chosen, show the quantity that actually backs
                          this pick so the clerk sees the real ceiling. */}
                      <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>
                        {!p.bin ? "Lots load after a bin is picked"
                          : p.lotMatrixTruncated ? "showing the 25 most recent lots — older ones aren't listed"
                          : p.loadingLotInBin ? "checking this lot in this bin…"
                          : lotInBinQty(p) != null ? (
                            <span style={{ color: lotInBinQty(p) > 0 ? "#059669" : "#B45309", fontWeight: 600 }}>
                              {lotInBinQty(p) > 0
                                ? `${fmtQty(lotInBinQty(p))} of lot ${p.lot} in bin ${p.bin}`
                                : `none of lot ${p.lot} in bin ${p.bin}`}
                            </span>
                          ) : `${(p.lots || []).length} lot(s) in this bin`}
                      </div>
                    </div>
                  </div>
                )}

                {/* Say what's wrong on the line the clerk is looking at, before
                    the pack is created — not as an Epicor rejection afterwards. */}
                {!locked && pickProblem(row) && (
                  <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: "#B45309" }}>
                    ⚠ {pickProblem(row)}
                  </div>
                )}

                {/* Why auto-select left this line alone (cleared on a manual pick). */}
                {!locked && p.autoNote && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#92400E" }}>
                    ⚡ {p.autoNote}
                  </div>
                )}
              </div>
            );
          })}

          {/* After a rejected line-add the pack header already exists. Say so,
              so the clerk fixes the pick and resumes instead of assuming the
              whole attempt has to be started over. */}
          {packNum && pendingLineCount > 0 && (
            <div style={{ borderRadius: 12, border: "1px solid #FDE68A", background: "#FFFBEB", padding: "10px 12px", fontSize: 12, color: "#92400E", marginBottom: 12 }}>
              Pack <strong>#{packNum}</strong> {packAdopted ? "was reused" : "was created"} and {createdLines.length === 0
                ? "no lines were accepted yet"
                : <>{createdLines.length} line{createdLines.length === 1 ? "" : "s"} already landed (locked below)</>}.
              Fix the flagged line{pendingLineCount === 1 ? "" : "s"} and continue — the pack is reused, not recreated.
            </div>
          )}

          {!stage2Ready && !packNum && !shippableRows.some((r) => picks[r.key]?.checked) && (
            <div style={{ fontSize: 12, color: "#92400E", fontWeight: 600, marginBottom: 10 }}>
              ⚠ Tick at least one line to ship.
            </div>
          )}

          <div className="qw-actions" style={{ display: "flex", gap: 10, marginTop: 6 }}>
            <Button className="qw-btn qw-btn-outline" onClick={() => goStage(1)}>← Back</Button>
            <Button className="qw-btn qw-btn-primary" style={{ flex: 1, background: PURPLE, borderColor: "transparent" }}
              // Once a pack exists we still gate on stage2Ready whenever lines
              // are outstanding — otherwise a failed line-add left the button
              // reading "Continue to review" and walked an EMPTY pack to
              // Stage 3, ready to buy a label for nothing.
              // UI-SHP-035: with no pack yet and nothing ticked there is nothing
              // to add, and a click would create an EMPTY pack in Epicor.
              disabled={creating || (pendingLineCount > 0 && !stage2Ready)
                || (!packNum && !shippableRows.some((r) => picks[r.key]?.checked))}
              onClick={createPackAndLines}>
              {creating ? (<><Spinner animation="border" size="sm" /> {packNum ? "Adding lines…" : reusablePack ? "Reusing pack…" : "Creating pack…"}</>)
                : pendingLineCount === 0 && packNum ? "Continue to review →"
                : packNum ? `Add ${pendingLineCount} remaining line${pendingLineCount === 1 ? "" : "s"} →`
                : reusablePack ? `Reuse pack #${reusablePack.packNum} + add lines →`
                : "Create pack + add lines →"}
            </Button>
          </div>
        </div>
      )}

      {/* ============ STAGE 3: REVIEW ============ */}
      {stage === 3 && (
        <div className="qw-card-flat qw-pad">
          <div className="qw-eyebrow">Review &amp; readiness</div>
          <div className="qw-h2" style={{ marginBottom: 4 }}>Pack <span className="qw-mono" style={{
            // Simulator's .brand-text-gradient treatment for the pack number.
            background: `linear-gradient(135deg, ${PURPLE}, ${ORANGE})`,
            WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
          }}>#{packNum}</span></div>
          <p style={{ color: "#64748B", fontSize: 13, marginBottom: 14 }}>
            Pre-flight check before we generate the label. Anything flagged below must be fixed first.
            <Button variant="link" size="sm" style={{ padding: "0 6px", fontSize: 12 }} onClick={() => loadReadiness()}>
              <FaSyncAlt size={11} /> re-check
            </Button>
          </p>

          {readinessLoading ? (
            <div style={{ textAlign: "center", padding: 32, color: "#64748B" }}><Spinner animation="border" size="sm" /> Running readiness check…</div>
          ) : !readiness ? (
            <Alert variant="warning" style={{ fontSize: 13 }}>Readiness not loaded. <Button variant="link" size="sm" onClick={() => loadReadiness()}>Retry</Button></Alert>
          ) : (
            <>
              {alreadyLabeled && (
                <Alert variant="warning" style={{ fontSize: 13 }}>
                  <FaExclamationTriangle style={{ marginRight: 6 }} />
                  This pack already has tracking <strong>{readiness.existingTrackingNumber}</strong>. Re-shipping needs
                  the <em>Force re-ship</em> toggle on the next screen (auto-voids the old label).
                </Alert>
              )}

              {/* The simulator's three pre-flight tiles. */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 14 }}>
                <ReadinessTile
                  ok={readiness.shipperReady}
                  title="Shipper"
                  source={readiness.shipperSource}
                  detail={readiness.shipperReady
                    ? "Plant defaults loaded"
                    : `Missing: ${(readiness.shipperMissing || []).join(", ") || "plant defaults"}`}
                />

                <ReadinessTile
                  ok={recipientReady}
                  title="Recipient"
                  source={readiness.recipientSource}
                  // The edit is checked FIRST (UI-SHP-042): a complete edited address is what the
                  // label will carry, so it must say so even when the order's own ship-to was
                  // already complete — that case used to read "Address complete".
                  detail={hasRecipientOverride
                    ? "Override ready — will be sent with the label"
                    : readiness.recipientReady
                      ? "Address complete"
                      : `Missing: ${(readiness.recipientMissing || []).join(", ") || "needs attention"}`}
                >
                  {!readiness.recipientReady && !phonePatched && (
                    <button style={linkBtn} onClick={patchPhone}>Patch phone →</button>
                  )}
                  <button style={linkBtn} onClick={() => setAddrOpen((v) => !v)}>
                    {addrOpen ? "Close editor" : "Edit address →"}
                  </button>
                </ReadinessTile>

                <ReadinessTile
                  ok={packageSatisfied}
                  title="Package"
                  detail={packageSatisfied
                    ? `Weight ${fmtQty(pkg.weight)} ${pkg.weightUOM || "LB"}`
                      + ((Number(pkg.length) > 0 || Number(pkg.width) > 0 || Number(pkg.height) > 0)
                        ? ` · ${fmtQty(pkg.length) || "?"}×${fmtQty(pkg.width) || "?"}×${fmtQty(pkg.height) || "?"} ${(readiness.pkgSizeUOM || "IN").toLowerCase()}`
                        : " · no dimensions on the pack")
                    : "Weight required — FedEx rates on it"}
                >
                  <button style={linkBtn} onClick={() => setPkgOpen((v) => !v)}>
                    {pkgOpen ? "Done" : packageSatisfied ? "Override →" : "Set weight →"}
                  </button>
                </ReadinessTile>
              </div>

              {/* Recipient address editor.
                  The panel takes its tone from the CURRENT verdict, using the same three
                  colours AddressVerdict uses, so the frame and the banner inside it can never
                  disagree. It used to be hardcoded amber, which meant that after adopting
                  FedEx's own corrected address you were left looking at a green "FedEx
                  verified this address" tick inside a warning-coloured box — and a warning
                  frame around a resolved address reads as "something is still wrong". */}
              {addrOpen && (() => {
                const v = addrValidate?.result;
                const editorTone = !v
                  // Nothing checked yet (or the check itself failed): neutral, not alarming.
                  // Amber here would warn about a problem nobody has found.
                  ? { border: "#CBD5E1", bg: "#F8FAFC66", head: "#334155" }
                  : v.isValid
                    ? { border: "#BBF7D0", bg: "#F0FDF466", head: "#14532D" }
                    : { border: "#FDE68A", bg: "#FFFBEB66", head: "#92400E" };
                return (
                <div style={{ borderRadius: 12, border: `2px solid ${editorTone.border}`, background: editorTone.bg, padding: 14, marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <div style={{ fontWeight: 600, color: editorTone.head, fontSize: 14 }}>Edit recipient</div>
                    <button style={{ border: "none", background: "none", color: "#94A3B8", fontSize: 18, cursor: "pointer" }} onClick={() => setAddrOpen(false)}>×</button>
                  </div>
                  <div className="qw-grid-2">
                    <RecipField label="Company / Person" val={recip.personName} on={(v) => setRecip({ ...recip, personName: v })} />
                    <RecipField label="Phone" mono val={recip.phoneNumber} on={(v) => setRecip({ ...recip, phoneNumber: v })} />
                    <RecipField label="Street" val={recip.street} on={(v) => setRecip({ ...recip, street: v })} onBlur={runAddressValidate} />
                    <RecipField label="City" val={recip.city} on={(v) => setRecip({ ...recip, city: v })} onBlur={runAddressValidate} />
                    <RecipField label="State" mono val={recip.state} on={(v) => setRecip({ ...recip, state: v })} onBlur={runAddressValidate} />
                    <RecipField label="Postal" mono val={recip.zip} on={(v) => setRecip({ ...recip, zip: v })} onBlur={runAddressValidate} />
                    <RecipField label="Country" mono val={recip.country} on={(v) => setRecip({ ...recip, country: v })} />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <AddressVerdict state={addrValidate} onApply={applyResolvedAddress} />
                  </div>
                </div>
                );
              })()}

              {/* Same verdict, for the clerk who never opened the editor —
                  which is the common path, and the one that used to ship
                  entirely unchecked. */}
              {!addrOpen && (
                <div style={{ marginBottom: 14 }}>
                  <AddressVerdict state={addrValidate} onApply={applyResolvedAddress} />
                </div>
              )}


              {/* Package editor — behind the Package tile's "Override →". */}
              {pkgOpen && (
              <div style={{ borderRadius: 12, border: "2px solid #E5E7EB", background: "#FAFAFB", padding: 14, marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, color: "#475569", fontSize: 14 }}>Override package weight &amp; dimensions</div>
                  <button style={{ border: "none", background: "none", color: "#94A3B8", fontSize: 18, cursor: "pointer" }} onClick={() => setPkgOpen(false)}>×</button>
                </div>
                {/* One row, six controls, all the same height — the package drives the
                    three dimension fields and weight is always the clerk's to enter. */}
                <div className="qw-pkgrow">
                  {/* The column span lives in QuoteWizard.css, NOT inline: an inline
                      grid-column outranks every media query, so the phone breakpoint
                      could not collapse this to one column and a phantom second track
                      appeared instead. */}
                  <div className="qw-pkgrow-pkg">
                    <label className="qw-label">Package</label>
                    <Form.Select
                      size="sm"
                      className="qw-input qw-mono"
                      value={pkgCode}
                      onChange={(e) => applyPackage(e.target.value)}
                      disabled={pkgCatalog.busy}
                    >
                      <option value="">
                        {pkgCatalog.busy ? "Loading packages…" : "Select a package…"}
                      </option>
                      {PKG_GROUPS.map(([label, fill]) => {
                        const rows = pkgCatalog.items.filter((p) => p.fill === fill);
                        if (!rows.length) return null;
                        return (
                          <optgroup key={fill} label={label}>
                            {rows.map((p) => (
                              <option key={p.code} value={p.code}>{pkgOptionLabel(p)}</option>
                            ))}
                          </optgroup>
                        );
                      })}
                    </Form.Select>
                  </div>
                  <PkgField label="Weight *" val={pkg.weight} on={(v) => setPkg({ ...pkg, weight: v })} />
                  <div style={{ minWidth: 0 }}>
                    <label className="qw-label">UOM</label>
                    <Form.Select size="sm" className="qw-input qw-mono" value={pkg.weightUOM} onChange={(e) => setPkg({ ...pkg, weightUOM: e.target.value })}>
                      <option value="LB">LB</option><option value="KG">KG</option>
                    </Form.Select>
                  </div>
                  <PkgField label="L (in)" val={pkg.length} on={(v) => setPkg({ ...pkg, length: v })} readOnly={dimsLocked.L} />
                  <PkgField label="W (in)" val={pkg.width} on={(v) => setPkg({ ...pkg, width: v })} readOnly={dimsLocked.W} />
                  <PkgField label="H (in)" val={pkg.height} on={(v) => setPkg({ ...pkg, height: v })} readOnly={dimsLocked.H} />
                </div>

                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 8 }}>{pkgHint}</div>

                {/* Dimensional weight, stated before the money is spent.
                    FedEx bills the GREATER of actual and volumetric weight, so on a
                    large light parcel the carton sets the price and the goods do not.
                    Measured on the live account: the same 5 lb parcel is $11.51 in a
                    6-inch box and $32.84 in a 24-inch cube. Saying which one is about
                    to bill turns that from a surprise on the invoice into a choice. */}
                {dimWeight > 0 && (
                  <div style={{ fontSize: 11.5, marginTop: 6, color: dimWeight > Number(pkg.weight || 0) ? "#92400E" : "#64748B" }}>
                    {dimWeight > Number(pkg.weight || 0)
                      ? `FedEx will bill ${dimWeight} LB — this box's dimensional weight, not the ${fmtQty(pkg.weight) || "0"} LB of goods.`
                      : `Dimensional weight ${dimWeight} LB — under the actual weight, so FedEx bills the actual.`}
                  </div>
                )}

                {pkgCatalog.stale && (
                  <div style={{ fontSize: 11.5, marginTop: 6, color: "#92400E" }}>
                    Package list couldn't be refreshed — showing the last copy we held. Dimensions are still correct for these cartons.
                  </div>
                )}
              </div>
              )}

              {/* Pack contents */}
              <div style={{ borderRadius: 12, background: "#FAFAFB", padding: 14, marginBottom: 16 }}>
                <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Pack contents</div>
                {(createdLines.length ? createdLines : []).map((l) => (
                  <div key={`${l.orderLine}-${l.orderRelNum}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
                    <span><span className="qw-mono" style={{ color: "#94A3B8" }}>L{l.orderLine}·{l.orderRelNum}</span> {l.partNum} · qty {fmtQty(l.qty)}{l.bin ? ` · bin ${l.bin}` : ""}{l.lot ? ` · lot ${l.lot}` : ""}</span>
                    <span style={{ color: "#166534" }}>✓</span>
                  </div>
                ))}
              </div>

              {readiness.blockers && readiness.blockers.length > 0 && !phonePatched && (
                <Alert variant="warning" style={{ fontSize: 13 }}>
                  <strong>Blockers:</strong> {readiness.blockers.join(" · ")}
                </Alert>
              )}

              {/* A half-filled override would replace the pack's good ShipTo
                  wholesale and fail at RateShop, so say exactly what's missing. */}
              {recipientOverridePartial && (
                <div style={{ borderRadius: 12, border: "1px solid #FDE68A", background: "#FFFBEB", padding: "10px 12px", fontSize: 12, color: "#92400E", marginBottom: 12 }}>
                  A recipient override replaces the whole address — partial edits can't be sent.
                  Complete every field, or close the editor to ship to the order's ship-to as-is.
                  Missing: <strong>{[
                    !recip.street.trim() && "street",
                    !recip.city.trim() && "city",
                    !recip.state.trim() && "state",
                    !recip.zip.trim() && "postal code",
                    !recip.country.trim() && "country",
                    !recip.phoneNumber.trim() && "phone",
                  ].filter(Boolean).join(", ")}</strong>.
                </div>
              )}

              <div className="qw-actions" style={{ display: "flex", gap: 10 }}>
                <Button className="qw-btn qw-btn-outline" onClick={() => goStage(2)}>← Back</Button>
                <Button className="qw-btn qw-btn-primary" style={{ flex: 1, background: PURPLE, borderColor: "transparent" }}
                  disabled={!canContinueToService} onClick={() => goStage(4)}>
                  Continue to service &amp; billing →
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ============ STAGE 4: SERVICE + BILLING ============ */}
      {stage === 4 && (
        <div className="qw-card-flat qw-pad">
          <div className="qw-eyebrow">Carrier service &amp; billing</div>
          <div className="qw-h2" style={{ marginBottom: 4 }}>Final confirmation</div>
          <p style={{ color: "#64748B", fontSize: 13, marginBottom: 16 }}>
            Defaults are set from the order. Override only what you actually need to change — the orchestrator validates everything.
          </p>

          {/* Buying a label is an irreversible spend, and until now no screen after the
              review step showed WHERE the parcel was going — service and billing only. So a
              clerk could not tell by looking whether the corrected address or the original
              ship-to was about to be printed. This states it plainly, and says which. */}
          <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Shipping to</div>
          <div
            className="qw-card-flat"
            style={{
              padding: "12px 14px", marginBottom: 18, fontSize: 13, lineHeight: 1.55,
              borderColor: hasRecipientOverride ? "#C7D2FE" : "#E2E8F0",
              background: hasRecipientOverride ? "#EEF2FF" : "#fff",
            }}
          >
            <div style={{ fontWeight: 700, color: "#0F172A" }}>
              {[recip.companyName, recip.personName].filter(Boolean).join(" · ") || "—"}
            </div>
            <div className="qw-mono" style={{ color: "#334155", marginTop: 2 }}>
              {[recip.street, [recip.city, recip.state].filter(Boolean).join(", "), recip.zip, recip.country]
                .filter(Boolean).join(" · ") || "—"}
            </div>
            {recip.phoneNumber && (
              <div style={{ color: "#64748B", fontSize: 12, marginTop: 2 }}>{recip.phoneNumber}</div>
            )}
            <div style={{ marginTop: 8, fontSize: 11.5 }}>
              {hasRecipientOverride ? (
                <span style={{ color: "#3730A3", fontWeight: 700 }}>
                  &#9998; Edited here — this replaces the order&rsquo;s ship-to on the label.
                </span>
              ) : (
                <span style={{ color: "#64748B" }}>From the order&rsquo;s ship-to, unchanged.</span>
              )}
              {recipientOverridePartial && (
                <span style={{ color: "#B45309", fontWeight: 700, marginLeft: 8 }}>
                  Incomplete — the order&rsquo;s ship-to will be used instead.
                </span>
              )}
            </div>
          </div>

          <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Shipping service</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
            {services.length === 0 && (
              <div style={{ fontSize: 13, color: "#94A3B8" }}>Service catalog unavailable — the pack's default Ship Via will be used.</div>
            )}
            {coreServices.map(renderServiceCard)}

            {/* The catalog is 14 services; the simulator surfaces the four Shukla
                actually sells and tucks the rest away. Same idea here. */}
            {moreServices.length > 0 && (
              <details>
                <summary style={{ fontSize: 12, fontWeight: 600, color: "#64748B", cursor: "pointer", padding: "4px 2px" }}>
                  {moreServices.length} more FedEx service{moreServices.length === 1 ? "" : "s"}
                </summary>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                  {moreServices.map(renderServiceCard)}
                </div>
              </details>
            )}

            {/* Ship-Via mapping is a post-label bookkeeping concern, never a
                blocker — surface it as a note on the chosen service instead. */}
            {selectedService && !selectedService.supported && (
              <div style={{ borderRadius: 12, border: "1px solid #FDE68A", background: "#FFFBEB", padding: "10px 12px", fontSize: 12, color: "#92400E" }}>
                <strong>{selectedService.serviceName}</strong> has no Epicor Ship Via mapping configured for {paymentType} billing.
                The label still buys and tracking/cost/weight still stamp — only <span className="qw-mono">FreightedShipViaCode</span> is
                left blank for a clerk to fill in.
              </div>
            )}
          </div>

          <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Billing</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginBottom: 12 }}>
            {PAYMENT_TYPES.map((pt) => {
              const sel = paymentType === pt.value;
              return (
                <label key={pt.value} style={{ textAlign: "center", padding: 12, borderRadius: 12, cursor: "pointer", border: `2px solid ${sel ? SEL_BORDER : IDLE_BORDER}`, background: sel ? SEL_BG : "#fff" }}>
                  <input type="radio" name="pay" checked={sel} onChange={() => setPaymentType(pt.value)} style={{ display: "none" }} />
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{pt.label}</div>
                  <div style={{ fontSize: 11, color: "#94A3B8" }}>{pt.hint}</div>
                </label>
              );
            })}
          </div>

          {NEEDS_PAYOR_ACCOUNT(paymentType) && (
            <div style={{ borderRadius: 12, border: "2px solid #FDE68A", background: "#FFFBEB66", padding: 12, marginBottom: 14 }}>
              <label className="qw-label" style={{ color: "#92400E" }}>Payor FedEx account number</label>
              <Form.Control size="sm" className="qw-input qw-mono" placeholder="e.g. 510051408" value={payorAccount}
                onChange={(e) => setPayorAccount(e.target.value)} />
              <div style={{ fontSize: 12, color: "#92400E", marginTop: 4 }}>Customer's FedEx account — required for THIRD_PARTY / RECIPIENT / COLLECT billing.</div>
            </div>
          )}

          <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Label reference (optional)</div>
          <Form.Control size="sm" className="qw-input" value={customerRef} onChange={(e) => setCustomerRef(e.target.value)}
            placeholder="e.g. PO-145941 or Patient 247" style={{ marginBottom: 16 }} />

          <details style={{ borderRadius: 12, border: "1px solid #EEF0F3", padding: 12, marginBottom: 16 }}>
            <summary style={{ fontSize: 12, fontWeight: 600, color: "#64748B", cursor: "pointer", textTransform: "uppercase" }}>Advanced</summary>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={forceReship} onChange={(e) => setForceReship(e.target.checked)} style={{ marginTop: 3, accentColor: PURPLE }} />
              <span>
                <strong>Force re-ship</strong> · auto-void any existing label before re-buying. Use only when re-shipping after a problem.
                {alreadyLabeled && <span style={{ color: "#92400E" }}> This pack is already labeled; you must enable this to re-ship.</span>}
              </span>
            </label>
          </details>

          <div style={{ borderRadius: 12, background: "#FAFAFB", padding: 12, fontSize: 12, color: "#64748B", marginBottom: 16 }}>
            <div style={{ fontWeight: 600, color: "#475569", marginBottom: 2 }}>📌 Pricing note</div>
            Cost comes from FedEx at label-buy time and is recorded on <span className="qw-mono">ShipHead.OutboundCost_c</span>.
            Service upgrades = company absorbs the difference (not re-billed). Billing variant flips which FedEx account pays at the carrier level.
          </div>

          <div className="qw-actions" style={{ display: "flex", gap: 10 }}>
            <Button className="qw-btn qw-btn-outline" onClick={() => goStage(3)}>← Back</Button>
            <Button className="qw-btn qw-btn-primary" style={{ flex: 1, background: ORANGE, borderColor: "transparent" }}
              disabled={payorMissing || (alreadyLabeled && !forceReship)} onClick={runGenerate}>
              {/* Orange is deliberate and unique to this control — it is the one
                  irreversible action in the wizard (buys a real FedEx label). */}
              ✓ Generate label + pack slip
            </Button>
          </div>
          {payorMissing && <div style={{ fontSize: 12, color: "#92400E", marginTop: 8, fontWeight: 600 }}>Enter the payor FedEx account number to continue.</div>}
        </div>
      )}

      {/* ============ STAGE 5: GENERATE / DONE ============ */}
      {stage === 5 && (
        <div className="qw-card-flat qw-pad">
          {genError ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <FaExclamationTriangle size={26} color="#DC2626" />
                <div>
                  <div className="qw-eyebrow" style={{ color: "#991B1B" }}>Shipment failed</div>
                  <div className="qw-h2">Nothing was shipped</div>
                </div>
              </div>
              <Alert variant="danger" style={{ fontSize: 13 }}><ErrorBody raw={genError} /></Alert>
              <p style={{ fontSize: 13, color: "#64748B" }}>
                The orchestrator auto-compensates: if a label was bought before the failure, it was voided. You can adjust the
                service/billing and try again.
              </p>
              <div className="qw-actions" style={{ display: "flex", gap: 10 }}>
                <Button className="qw-btn qw-btn-outline" onClick={() => goStage(4)}>← Back to service</Button>
                <Button className="qw-btn qw-btn-primary" style={{ flex: 1, background: ORANGE, borderColor: "transparent" }} onClick={runGenerate}>
                  <FaSyncAlt style={{ marginRight: 6 }} /> Retry
                </Button>
              </div>
            </>
          ) : !result ? (
            // Generating view
            <>
              <div className="qw-eyebrow">Generating shipment</div>
              <div className="qw-h2" style={{ marginBottom: 6 }}>Atomically committing…</div>
              <p style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>
                One transaction, six steps. The orchestrator handles BPM soft-fail recovery and label compensation automatically.
              </p>
              <div style={{ borderRadius: 12, border: "1px solid #EEF0F3", padding: 16 }}>
                {GEN_STEPS.map((s, i) => {
                  const state = i < genStep ? "done" : i === genStep ? "active" : "pending";
                  return (
                    <div key={s.title} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", opacity: state === "pending" ? 0.45 : 1 }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, background: state === "done" ? "#16A34A" : state === "active" ? PURPLE : "#D4D4D8" }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{i + 1} · {s.title}</div>
                        <div style={{ fontSize: 12, color: "#94A3B8" }}>{s.sub}</div>
                      </div>
                      {state === "active" && <Spinner animation="border" size="sm" />}
                      {state === "done" && <FaCheckCircle color="#16A34A" />}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            // Done view
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#DCFCE7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaCheckCircle size={24} color="#16A34A" />
                </div>
                <div>
                  <div className="qw-eyebrow" style={{ color: "#166534" }}>Shipment complete</div>
                  <div className="qw-h2">Pack #{result.packNum} is {result.shipStatus}</div>
                </div>
              </div>

              <div className="qw-grid-2" style={{ marginTop: 16 }}>
                <div style={{ borderRadius: 12, border: "1px solid #EEF0F3", padding: 14 }}>
                  <div className="qw-eyebrow">Tracking</div>
                  <div className="qw-mono" style={{ fontWeight: 700, fontSize: 18 }}>{result.trackingNumber || "—"}</div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>{result.serviceName || result.serviceType} · {result.paymentType}</div>
                </div>
                <div style={{ borderRadius: 12, border: "1px solid #EEF0F3", padding: 14 }}>
                  <div className="qw-eyebrow">Freighted Ship Via</div>
                  <div className="qw-mono" style={{ fontWeight: 700, fontSize: 18 }}>{result.freightedShipViaCode || "—"}</div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
                    {freightLine(result)}
                  </div>
                </div>
              </div>

              {/* What went on the parcel, straight from the orchestrator's
                  response. Rendered but HIDDEN: it exists purely so
                  openLabelPreview can clone its markup (barcode SVG included)
                  into the popup. Inline it ate most of the Done screen and
                  pushed the action buttons below the fold. */}
              {(result.shipper || result.recipient) && (
                <div ref={labelPreviewRef} style={{ display: "none" }} aria-hidden="true">
                  <ShipmentLabelCard result={result} orderNum={orderNum} />
                </div>
              )}

              {/* The pack-slip card stays a real anchor: window.open() from
                  script is popup-blocked by default in Chrome/Edge, and an
                  <a target="_blank"> click never is. The label card is a button
                  because it opens a composed preview document rather than
                  navigating to a URL — safe because a click IS a user gesture,
                  which is what the blocker actually keys on. */}
              <div className="qw-grid-2" style={{ marginTop: 12 }}>
                {(result.shipper || result.recipient) ? (
                  <button type="button" onClick={openLabelPreview}
                    style={{ ...docBtn("#DDD6FE"), textAlign: "left", fontFamily: "inherit", color: "inherit" }}>
                    <span style={docIcon("#EDE9FE")}><FaFilePdf color="#7C3AED" /></span>
                    <div style={{ flex: 1, textAlign: "left" }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>FedEx Label ↗</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>
                        {result.trackingNumber ? `Click to open · ${result.trackingNumber}` : "Click to open"}
                      </div>
                    </div>
                  </button>
                ) : result.labelDownloadUrl ? (
                  <a href={result.labelDownloadUrl} target="_blank" rel="noopener noreferrer"
                    style={{ ...docBtn("#DDD6FE"), textDecoration: "none", color: "inherit" }}>
                    <span style={docIcon("#EDE9FE")}><FaFilePdf color="#7C3AED" /></span>
                    <div style={{ flex: 1, textAlign: "left" }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>FedEx Label PDF ↗</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>Click to open</div>
                    </div>
                  </a>
                ) : (
                  <div style={{ ...docBtn("#EEF0F3"), cursor: "default", opacity: .6 }}>
                    <span style={docIcon("#EDE9FE")}><FaFilePdf color="#7C3AED" /></span>
                    <div style={{ flex: 1, textAlign: "left" }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>FedEx Label PDF</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>Not available yet</div>
                    </div>
                  </div>
                )}

                {slip.status === "ready" && slip.url ? (
                  <a href={slip.url} target="_blank" rel="noopener noreferrer"
                    style={{ ...docBtn("#FED7AA"), textDecoration: "none", color: "inherit" }}>
                    <span style={docIcon("#FFEDD5")}><FaFileInvoice color="#EA580C" /></span>
                    <div style={{ flex: 1, textAlign: "left" }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>Pack Slip PDF ↗</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>Click to open</div>
                    </div>
                  </a>
                ) : (
                  <div style={{ ...docBtn("#EEF0F3"), cursor: "default", opacity: .6 }}>
                    <span style={docIcon("#FFEDD5")}><FaFileInvoice color="#EA580C" /></span>
                    <div style={{ flex: 1, textAlign: "left" }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>Pack Slip PDF</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>
                        {slip.status === "polling" ? "Preparing…"
                          : slip.status === "timedout" ? "Taking longer than usual — retry below"
                          : slip.status === "rejected" ? "Couldn't be generated — retry below"
                          : "—"}
                        {slip.status === "polling" && <Spinner animation="border" size="sm" style={{ marginLeft: 6 }} />}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, fontSize: 12, color: "#64748B", cursor: "pointer" }}>
                <input type="checkbox" checked={autoOpenLabel} style={{ accentColor: PURPLE }}
                  onChange={(e) => setAutoOpenLabel(e.target.checked)} />
                <span>Open the label in a new tab as soon as it's ready</span>
              </label>

              {(slip.status === "timedout" || slip.status === "rejected") && (
                <div style={{ borderRadius: 12, border: "1px solid #FDE68A", background: "#FFFBEB", padding: "10px 12px", marginTop: 10, fontSize: 12, color: "#92400E", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ flex: 1, minWidth: 240 }}>
                    {slip.status === "timedout"
                      ? "The packing slip is taking longer than usual to prepare."
                      : "The packing slip couldn't be generated."}
                    {" "}The shipment is complete and the label is ready — only the document is missing,
                    and trying again is safe.
                  </span>
                  <Button className="qw-btn qw-btn-sm" style={{ background: PURPLE, borderColor: "transparent", color: "#fff" }}
                    onClick={retryPackSlip}>
                    <FaSyncAlt style={{ marginRight: 6 }} /> Retry packing slip
                  </Button>
                </div>
              )}

              {(result.warnings?.length > 0 || result.compensationReason) && (
                <div style={{ borderRadius: 12, background: "#FFFBEB", border: "1px solid #FDE68A", padding: 14, marginTop: 14 }}>
                  <div style={{ fontWeight: 600, color: "#92400E", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                    <FaExclamationTriangle /> Recovery notes (informational)
                  </div>
                  {/* Warnings carry an Epicor failure reason verbatim, so they
                      contain the same DEBUG CONTEXT dump the error paths do —
                      route them through the same splitter. */}
                  {result.compensationReason && (
                    <div style={{ fontSize: 12, color: "#92400E", marginTop: 6 }}>
                      <ErrorBody raw={`Compensation: ${result.compensationReason}`} />
                    </div>
                  )}
                  {(result.warnings || []).map((w, i) => (
                    <div key={i} style={{ fontSize: 12, color: "#92400E", marginTop: 6 }}>
                      <ErrorBody raw={w} />
                    </div>
                  ))}
                </div>
              )}

              <div style={{ borderRadius: 12, background: "#FAFAFB", padding: 14, marginTop: 14 }}>
                <div className="qw-eyebrow" style={{ marginBottom: 6 }}>ErpJob audit chain</div>
                <div className="qw-mono" style={{ fontSize: 12, color: "#475569", lineHeight: 1.7 }}>
                  <div><span style={{ color: "#94A3B8" }}>Parent</span> {result.jobId}</div>
                  {result.rateShopJobId && <div style={{ paddingLeft: 16 }}>└ rateShop: {result.rateShopJobId}</div>}
                  {result.labelJobId && <div style={{ paddingLeft: 16 }}>└ label: {result.labelJobId}</div>}
                  {result.markShippedJobId && <div style={{ paddingLeft: 16 }}>└ markShipped: {result.markShippedJobId}</div>}
                </div>
              </div>

              <div className="qw-actions" style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <Button className="qw-btn qw-btn-outline" onClick={resetToStart}>← Back to order</Button>
                {result.readyToInvoice && (
                  <Button className="qw-btn qw-btn-primary" style={{ flex: 1, background: PURPLE, borderColor: "transparent" }}
                    onClick={() => navigate(`/order/${encodeURIComponent(orderNum)}`)}>
                    Done · ready to invoice
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tiny field helpers ────────────────────────────────────────────────────────
function Meta({ label, value, sub }) {
  return (
    <div>
      <div className="qw-eyebrow">{label}</div>
      <div style={{ fontWeight: 600 }}>{value || "—"}</div>
      {sub && <div style={{ fontSize: 12, color: "#94A3B8" }}>{sub}</div>}
    </div>
  );
}
function RecipField({ label, val, on, onBlur, mono }) {
  return (
    <div>
      <label className="qw-label">{label}</label>
      <Form.Control size="sm" className={`qw-input${mono ? " qw-mono" : ""}`} value={val || ""}
        onChange={(e) => on(e.target.value)} onBlur={onBlur} />
    </div>
  );
}
function PkgField({ label, val, on, readOnly = false }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label className="qw-label">{label}</label>
      <Form.Control
        size="sm" type="number"
        className={`qw-input qw-mono${readOnly ? " qw-input-locked" : ""}`}
        value={val}
        readOnly={readOnly}
        // A locked field is answered, not disabled: it stays focusable and
        // readable by a screen reader, and the value is still submitted. Only
        // typing into it is refused, and the hint beneath says how to change it.
        aria-readonly={readOnly || undefined}
        onChange={(e) => { if (!readOnly) on(e.target.value); }}
      />
    </div>
  );
}

// Groups for the package dropdown, ordered by how much the clerk still has to do.
// The `fill` values come from the server (PackageOption.Fill) so the classification
// lives in exactly one place — see PackingRepository.Map for why zero dimensions,
// and not the PromptSet* flags, decide it.
const PKG_GROUPS = [
  ["Cartons — smallest first", "Sized"],
  ["Pallets — height varies", "Pallet"],
  ["No preset size", "Custom"],
];

/// Label for one dropdown row. Sized cartons show their real dimensions so the
/// clerk can match the box in front of them without opening anything; pallets show
/// the footprint with the height left visibly open; unsized rows fall back to
/// Epicor's own description ("? x ? x ?", "Box, Other").
function pkgOptionLabel(p) {
  if (p.fill === "Sized") return `${p.code} · ${fmtQty(p.length)} × ${fmtQty(p.width)} × ${fmtQty(p.height)}`;
  if (p.fill === "Pallet") return `${p.code} · ${fmtQty(p.length)} × ${fmtQty(p.width)} × ?`;
  return `${p.code} · ${p.description}`;
}

const linkBtn = { border: "none", background: "none", padding: 0, color: "inherit", fontSize: 12, fontWeight: 600, textDecoration: "underline", cursor: "pointer" };
function docBtn(border) {
  return { display: "flex", gap: 12, alignItems: "center", padding: 14, borderRadius: 12, border: `2px solid ${border}`, background: "#fff", cursor: "pointer", width: "100%" };
}
function docIcon(bg) {
  return { width: 40, height: 40, borderRadius: 10, background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
}

// Decode a base64 PDF payload into an object URL for window.open.
